/**
 * ДДС — единая лента приходов и расходов в две колонки.
 * Фильтры: дата, объект, категории расходов (мульти-чипы), ответственный.
 * Никаких раскрытий. Просто журнал транзакций как в Excel-листе ДДС.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { PeriodPicker } from "@/components/PeriodPicker";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { ExpenseFormModal } from "@/components/finance/ExpenseFormModal";
import { RevenueFormModal } from "@/components/finance/RevenueFormModal";
import {
  fetchTransactions, fetchObjects, fetchExpenseCategories, fetchResponsiblesGrouped,
  updateExpense, updateRevenue, createRevenue, createExpense, deleteRevenue, deleteExpense,
  formatRub, KIND_LABELS, KIND_COLORS, PAYMENT_LABELS, REVENUE_TYPE_LABELS,
  RESPONSIBLE_KIND_LABELS, RESPONSIBLE_KIND_COLORS,
  type TransactionRow, type CategoryKind, type ExpenseCategoryRow, type ObjectRow,
  type ResponsibleKind, type RevenuePayload, type PaymentMethod, type ExpensePayload,
  type ResponsibleGrouped, type RevenueRow, type ExpenseRow, type RevenueType,
} from "@/api/finance";
import { ApiError } from "@/api/client";
import { usePeriod } from "@/state/period";


/* Адаптеры TransactionRow → RevenueRow / ExpenseRow.
 * Нужны чтобы открыть RevenueFormModal / ExpenseFormModal на существующей
 * строке ДДС (которая возвращает облегчённый TransactionRow). Часть полей
 * (created_at/updated_at, accrual_id) формам не нужны для отображения. */
function txToRevenueRow(r: TransactionRow): RevenueRow {
  return {
    id: r.id,
    received_at: r.received_at || "",
    deal_amo_id: r.deal_amo_id ?? null,
    object_id: r.object_id,
    object_name: r.object_name,
    counterparty: r.counterparty ?? null,
    amount: r.amount,
    type: (r.type ?? "prepay") as RevenueType,
    payment_method: r.payment_method,
    account_id: r.account_id,
    account_name: r.account_name,
    region: r.region,
    note: r.note,
    responsible_kind: r.responsible_kind ?? null,
    responsible_user_id: r.responsible_user_id ?? null,
    responsible_user_name: r.responsible_user_name ?? null,
    created_by_user_id: r.created_by_user_id,
    created_by_name: r.created_by_name,
    sales_manager_id: r.sales_manager_id ?? null,
    sales_manager_name: r.sales_manager_name ?? null,
    created_at: "",
    updated_at: "",
  };
}

function txToExpenseRow(r: TransactionRow): ExpenseRow {
  return {
    id: r.id,
    date: r.date || "",
    category_id: r.category_id ?? 0,
    category_name: r.category_name ?? null,
    category_kind: r.category_kind ?? null,
    secondary_category_id: r.secondary_category_id ?? null,
    secondary_category_name: r.secondary_category_name ?? null,
    secondary_category_kind: r.secondary_category_kind ?? null,
    accrual_id: null,
    amount: r.amount,
    description: r.description ?? null,
    supplier: r.supplier ?? null,
    payment_method: r.payment_method,
    account_id: r.account_id,
    account_name: r.account_name,
    object_id: r.object_id,
    object_name: r.object_name,
    deal_amo_id: null,
    region: r.region,
    note: r.note,
    attachment_path: null,
    responsible_kind: r.responsible_kind ?? null,
    responsible_user_id: r.responsible_user_id ?? null,
    responsible_user_name: r.responsible_user_name ?? null,
    created_by_user_id: r.created_by_user_id,
    created_by_name: r.created_by_name,
    created_at: "",
    updated_at: "",
  };
}

/** Абсолютный URL карточки в производственном сабдомене
 *  (finance.<домен> → production.<домен>/production/{id}). */
function productionUrl(ppId: number): string {
  const base = window.location.hostname.split(".").slice(1).join(".");
  return `${window.location.protocol}//production.${base}/production/${ppId}`;
}

/** Пара иконок-ссылок: Производство (домик) + AmoCRM (внешняя). */
function ObjLinkIcons({ amoUrl, ppId }: { amoUrl?: string | null; ppId?: number | null }) {
  if (!amoUrl && !ppId) return null;
  return (
    <span className="no-print" style={{ display: "inline-flex", gap: 4, marginLeft: 5, verticalAlign: "middle" }}>
      {ppId ? (
        <a href={productionUrl(ppId)} target="_blank" rel="noopener noreferrer"
           onClick={e => e.stopPropagation()} title="Открыть карточку в Производстве"
           style={{ display: "inline-flex", alignItems: "center", color: "var(--green)" }}>
          <Icon name="home" size={12}/>
        </a>
      ) : null}
      {amoUrl ? (
        <a href={amoUrl} target="_blank" rel="noopener noreferrer"
           onClick={e => e.stopPropagation()} title="Открыть сделку в AmoCRM"
           style={{ display: "inline-flex", alignItems: "center", color: "var(--brick)" }}>
          <Icon name="external" size={12}/>
        </a>
      ) : null}
    </span>
  );
}

/* Инвалидация всех кэшей, зависящих от расходов/приходов. После любой
 * мутации в ДС (create/update/delete) данные могут поменяться в дивидендах,
 * P&L-объектах и т.д. — инвалидируем всё разом. */
function invalAllFinance(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["finance-transactions"] });
  void qc.invalidateQueries({ queryKey: ["dividend-preview-sheet"] });
  void qc.invalidateQueries({ queryKey: ["finance-overview"] });
  void qc.invalidateQueries({ queryKey: ["finance-objects"] });
  void qc.invalidateQueries({ queryKey: ["finance-objects-list"] });
  void qc.invalidateQueries({ queryKey: ["finance-expenses"] });
}

/* ── Resizable columns в ДДС ──────────────────────────────────────── */
const DDS_COL_KEYS = [
  "inc_date", "inc_object", "inc_manager", "inc_what", "inc_pay", "inc_resp", "inc_amount",
  "exp_date", "exp_object", "exp_what", "exp_pay", "exp_kind", "exp_resp", "exp_amount",
] as const;
type DdsColKey = typeof DDS_COL_KEYS[number];
const DDS_COL_DEFAULTS: Record<DdsColKey, number> = {
  inc_date: 84, inc_object: 130, inc_manager: 120, inc_what: 140, inc_pay: 80, inc_resp: 90, inc_amount: 95,
  exp_date: 84, exp_object: 130, exp_what: 150, exp_pay: 80, exp_kind: 90, exp_resp: 90, exp_amount: 95,
};
const DDS_STORAGE_KEY = "kb-finance-dds-cols-v6";
const DDS_VISIBILITY_KEY = "kb-finance-dds-visible-v2";
// По умолчанию колонки «Ответственный» (inc_resp/exp_resp) скрыты — юзер
// включает их в попапе «Колонки» только когда они реально нужны.
const DDS_DEFAULT_HIDDEN: ReadonlySet<string> = new Set(["inc_resp", "exp_resp"]);

const DDS_COL_LABELS: Record<DdsColKey, string> = {
  inc_date:   "Приход · Дата",
  inc_object: "Приход · Объект",
  inc_manager: "Приход · Менеджер",
  inc_what:   "Приход · За что",
  inc_pay:    "Приход · Оплата",
  inc_resp:   "Приход · Ответственный",
  inc_amount: "Приход · Сумма",
  exp_date:   "Расход · Дата",
  exp_object: "Расход · Объект",
  exp_what:   "Расход · Статья",
  exp_pay:    "Расход · Оплата",
  exp_kind:   "Расход · Категория",
  exp_resp:   "Расход · Ответственный",
  exp_amount: "Расход · Сумма",
};

function defaultVisibleSet(): Set<DdsColKey> {
  return new Set(DDS_COL_KEYS.filter(k => !DDS_DEFAULT_HIDDEN.has(k)));
}

function useDdsVisibleCols() {
  const [visible, setVisible] = useState<Set<DdsColKey>>(() => {
    try {
      const raw = localStorage.getItem(DDS_VISIBILITY_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) {
        const set = new Set(arr.filter((k): k is DdsColKey => (DDS_COL_KEYS as readonly string[]).includes(k)));
        // Auto-discover: добавляем новые колонки, которых не было в сохранённой версии,
        // чтобы они появились автоматически — но НЕ добавляем те, что по дефолту скрыты.
        const knownCols: ReadonlySet<string> = new Set<string>(arr);
        for (const k of DDS_COL_KEYS) {
          if (!knownCols.has(k) && !DDS_DEFAULT_HIDDEN.has(k)) set.add(k);
        }
        return set;
      }
    } catch { /* ignore */ }
    return defaultVisibleSet();
  });
  useEffect(() => {
    try { localStorage.setItem(DDS_VISIBILITY_KEY, JSON.stringify(Array.from(visible))); } catch { /* ignore */ }
  }, [visible]);
  function toggle(k: DdsColKey) {
    setVisible(s => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  }
  function reset() { setVisible(defaultVisibleSet()); }
  return { visible, toggle, reset };
}

function useDdsColumnWidths() {
  const [widths, setWidths] = useState<Record<DdsColKey, number>>(() => {
    try {
      const raw = localStorage.getItem(DDS_STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : {};
      return { ...DDS_COL_DEFAULTS, ...saved };
    } catch { return DDS_COL_DEFAULTS; }
  });
  useEffect(() => {
    try { localStorage.setItem(DDS_STORAGE_KEY, JSON.stringify(widths)); } catch { /* ignore */ }
  }, [widths]);
  function startResize(key: DdsColKey, e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[key];
    const onMove = (ev: MouseEvent) => {
      ev.preventDefault();
      const w = Math.max(40, startW + ev.clientX - startX);
      setWidths(prev => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }
  function resetWidths() { setWidths(DDS_COL_DEFAULTS); }
  return { widths, startResize, resetWidths };
}

/* Селект объектов с поиском по client_name + region.
 * Заменяет <select>, у которого нет поиска — а объектов десятки. */
function ObjectSearchPicker({ value, objects, onChange }: {
  value: number | "";
  objects: ObjectRow[];
  onChange: (v: number | "") => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = value !== "" ? objects.find(o => o.id === value) : null;
  const norm = (s: string) => s.toLowerCase().replace(/ё/g, "е");
  const q = norm(query.trim());
  const filtered = !q
    ? objects.slice(0, 80)
    : objects.filter(o => {
        const hay = norm(`${o.client_name || ""} ${o.region || ""} ${o.address || ""}`);
        return hay.includes(q);
      }).slice(0, 80);

  useEffect(() => { setHoverIdx(0); }, [query, open]);

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: "0 1 200px", minWidth: 150 }}>
      <div className="input-sm" style={{
        display: "flex", alignItems: "center", gap: 4, cursor: "text", padding: "2px 6px",
      }} onClick={() => setOpen(true)}>
        {selected ? (
          <>
            <span style={{ flex: 1, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.client_name}{selected.region ? ` · ${selected.region}` : ""}
            </span>
            <button type="button"
                    onClick={e => { e.stopPropagation(); onChange(""); setQuery(""); setOpen(false); }}
                    style={{ background: "none", border: "none", color: "var(--mute)", cursor: "pointer", padding: 0, fontSize: 12 }}
                    title="Очистить"><Icon name="x" size={12}/></button>
          </>
        ) : (
          <input
            type="text" value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx(i => Math.min(filtered.length - 1, i + 1)); setOpen(true); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setHoverIdx(i => Math.max(0, i - 1)); }
              else if (e.key === "Enter") {
                e.preventDefault();
                const o = filtered[hoverIdx];
                if (o) { onChange(o.id); setQuery(""); setOpen(false); }
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            placeholder="все объекты…"
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 12, minWidth: 0 }}
          />
        )}
      </div>
      {open && !selected && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: 260,
          background: "var(--card)", border: "1px solid var(--line)",
          borderRadius: 6, boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
          zIndex: 30, maxHeight: 320, overflowY: "auto",
        }}>
          <div
            onMouseDown={() => { onChange(""); setOpen(false); setQuery(""); }}
            style={{
              padding: "7px 10px", fontSize: 12, color: "var(--mute)",
              cursor: "pointer", borderBottom: "1px solid var(--line-soft)",
            }}>
            — все объекты —
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--mute)" }}>
              Ничего не найдено по «{query}»
            </div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={o.id}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseDown={(e) => {
                  // Клик по иконкам-ссылкам не выбирает объект
                  if ((e.target as HTMLElement).closest("a")) return;
                  onChange(o.id); setQuery(""); setOpen(false);
                }}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  background: i === hoverIdx ? "var(--paper-2)" : undefined,
                  borderBottom: "1px solid var(--line-soft)",
                  fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {o.client_name}
                    {o.region && <span style={{ color: "var(--mute)", fontWeight: 400 }}> · {o.region}</span>}
                  </span>
                  <ObjLinkIcons amoUrl={o.amo_url} ppId={o.production_project_id}/>
                </div>
                {o.address && (
                  <div style={{ fontSize: 10.5, color: "var(--mute)", marginTop: 1 }}>{o.address}</div>
                )}
              </div>
            ))
          )}
          <div style={{ padding: "5px 10px", fontSize: 10, color: "var(--mute)", background: "var(--paper-2)", textAlign: "center" }}>
            {filtered.length} из {objects.length}{filtered.length === 80 && q.length === 0 ? " (показано первых 80)" : ""}
          </div>
        </div>
      )}
    </div>
  );
}

function ResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <span
      onMouseDown={onMouseDown}
      onClick={(e) => e.stopPropagation()}
      className="col-resize-handle"
      title="Перетащите чтобы изменить ширину колонки"
    />
  );
}


export function FinanceCashflowPage() {
  const { from, to, region } = usePeriod();
  const [objectId, setObjectId] = useState<number | "">("");
  // Мульти-фильтр по категориям (kind). Пустой набор = показывать все.
  const [kindFilter, setKindFilter] = useState<Set<CategoryKind>>(new Set());
  const [responsibleId, setResponsibleId] = useState<string>("");
  // Фильтр по статусу объекта движения: all | done (Выполнен) | working (В работе)
  const [statusFilter, setStatusFilter] = useState<"all" | "done" | "working">("all");

  // Авто-сворачивание сайдбара при заходе на ДДС — таблица широкая,
  // нужно максимум места. FinanceShell слушает этот событие. Юзер может
  // развернуть обратно через кнопку-toggle, если хочет.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("kb-sidebar-set-collapsed", { detail: true }));
  }, []);

  const objectsQ = useQuery({
    queryKey: ["finance-objects-list", region],
    queryFn: () => fetchObjects({ region: region !== "all" ? region : undefined }),
    staleTime: 5 * 60 * 1000,
  });
  const categoriesQ = useQuery({
    queryKey: ["finance-categories"],
    queryFn: () => fetchExpenseCategories(),
    staleTime: 5 * 60 * 1000,
  });
  const responsiblesQ = useQuery({
    queryKey: ["finance-responsibles-grouped"],
    queryFn: () => fetchResponsiblesGrouped(),
    staleTime: 5 * 60 * 1000,
  });

  const txQ = useQuery({
    queryKey: ["finance-transactions", from, to, objectId, responsibleId],
    queryFn: () => fetchTransactions({
      date_from: from, date_to: to,
      object_id: objectId === "" ? undefined : Number(objectId),
      responsible_user_id: responsibleId || undefined,
    }),
    staleTime: 60 * 1000,
  });

  // Статус-фильтр объекта — useMemo ДО любых ранних return (правила хуков).
  // Оставляем движения только по объектам нужного статуса, итоги пересчитываем.
  const filteredData = useMemo(() => {
    const rd = txQ.data;
    const kindsOn = kindFilter.size > 0;
    if (!rd || (statusFilter === "all" && !kindsOn)) return rd;
    const st = new Map<number, string>();
    for (const o of (objectsQ.data || [])) {
      const isDone = o.production_status === "completed" || !!o.actual_end || o.status === "completed";
      st.set(o.id, isDone ? "done"
                          : (o.production_status === "in_progress" || !!o.actual_start) ? "working"
                          : "other");
    }
    const ok = (oid: number | null | undefined) =>
      statusFilter === "all" || (oid != null && st.get(oid) === statusFilter);
    const okKind = (k: string | null | undefined) =>
      !kindsOn || kindFilter.has(((k || "other") as CategoryKind));
    const incomes = rd.incomes.filter(r => ok(r.object_id));
    const expenses = rd.expenses.filter(r => ok(r.object_id) && okKind(r.category_kind));
    return {
      ...rd,
      incomes, expenses,
      totals: {
        ...rd.totals,
        incomes_total: incomes.reduce((s, r) => s + r.amount, 0),
        incomes_count: incomes.length,
        expenses_total: expenses.reduce((s, r) => s + r.amount, 0),
        expenses_count: expenses.length,
      },
    };
  }, [txQ.data, statusFilter, kindFilter, objectsQ.data]);

  if (txQ.isLoading) return <Spinner size="lg" label="Загрузка ДДС…" />;
  if (txQ.error) {
    if (txQ.error instanceof ApiError && txQ.error.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ только для админа/руководителя</div></div>;
    }
    const msg = txQ.error instanceof ApiError ? `HTTP ${txQ.error.status}` : (txQ.error as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }
  if (!filteredData) return <Spinner size="lg" label="Загрузка ДДС…" />;
  const data = filteredData;
  const objects = objectsQ.data || [];
  const responsiblesGrouped = responsiblesQ.data || { engineers: [], owners: [] };
  const responsibles = [...responsiblesGrouped.engineers, ...responsiblesGrouped.owners];

  return (
    <FinanceLayout>
      
      {/* Фильтр периода — на уровне страницы (объект/статья/ответств. вынесены в шапку таблицы) */}
      <div className="filter-bar" style={{ flexWrap: "wrap", gap: 6, marginBottom: "var(--gap)", alignItems: "center" }}>
        <PeriodPicker />
        <div style={{ width: 1, height: 20, background: "var(--line)", margin: "0 6px" }}/>
        <label style={{ fontSize: 12, color: "var(--mute)" }}>Статус:</label>
        <button className={`chip ${statusFilter === "done" ? "active" : ""}`}
                onClick={() => setStatusFilter(s => s === "done" ? "all" : "done")}
                title="Только движения по выполненным объектам">
          Выполнен
        </button>
        <button className={`chip ${statusFilter === "working" ? "active" : ""}`}
                onClick={() => setStatusFilter(s => s === "working" ? "all" : "working")}
                title="Только движения по объектам в работе">
          В работе
        </button>
        <div style={{ width: 1, height: 20, background: "var(--line)", margin: "0 6px" }}/>
        <label style={{ fontSize: 12, color: "var(--mute)" }}>Категории:</label>
        {(["fot", "materials", "outsource", "taxes", "marketing", "sales", "other"] as CategoryKind[]).map(k => {
          const on = kindFilter.has(k);
          return (
            <button key={k} className={`chip ${on ? "active" : ""}`}
                    onClick={() => setKindFilter(prev => {
                      const n = new Set(prev);
                      if (n.has(k)) n.delete(k); else n.add(k);
                      return n;
                    })}
                    title={on ? "Убрать категорию из фильтра" : "Показывать эту категорию"}
                    style={on
                      ? { background: KIND_COLORS[k], borderColor: KIND_COLORS[k], color: "#fff" }
                      : { color: KIND_COLORS[k], borderColor: KIND_COLORS[k] }}>
              {KIND_LABELS[k]}
            </button>
          );
        })}
        {kindFilter.size > 0 && (
          <button className="chip" onClick={() => setKindFilter(new Set())}
                  title="Сбросить фильтр категорий — показывать все">
            ✕ Все
          </button>
        )}
      </div>

      {/* KPI — всегда в млн ₽ */}
      {(() => {
        const inMln = (v: number) => (v / 1_000_000).toFixed(1).replace(".", ",");
        const balance = data.totals.incomes_total - data.totals.expenses_total;
        const revenue = data.totals.incomes_total;
        // Разбивка расходов по category_kind
        const KIND_ORDER: CategoryKind[] = ["fot", "materials", "outsource", "taxes", "marketing", "sales", "other"];
        const byKind = new Map<CategoryKind, { sum: number; count: number }>();
        for (const e of data.expenses) {
          const k = (e.category_kind || "other") as CategoryKind;
          const cur = byKind.get(k) || { sum: 0, count: 0 };
          cur.sum += e.amount;
          cur.count += 1;
          byKind.set(k, cur);
        }
        const kindCards = KIND_ORDER
          .filter(k => (byKind.get(k)?.sum ?? 0) > 0)
          .map(k => ({ kind: k, sum: byKind.get(k)!.sum, count: byKind.get(k)!.count }));

        return (
          <>
            <div className="row row-3" style={{ marginBottom: "var(--gap)" }}>
              <div className="kpi good">
                <div className="kpi-label">Приходы</div>
                <div className="kpi-value kpi-value-sm">{inMln(data.totals.incomes_total)}<span className="kpi-unit"> млн ₽</span></div>
                <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>{data.totals.incomes_count} операций</span></div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Расходы</div>
                <div className="kpi-value kpi-value-sm" style={{ color: "var(--brick)" }}>{inMln(data.totals.expenses_total)}<span className="kpi-unit"> млн ₽</span></div>
                <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>{data.totals.expenses_count} операций</span></div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Сальдо</div>
                <div className="kpi-value kpi-value-sm" style={{ color: balance >= 0 ? "var(--green)" : "var(--brick)" }}>
                  {inMln(balance)}<span className="kpi-unit"> млн ₽</span>
                </div>
              </div>
            </div>

            {/* Разбивка расходов по типам — по 3 в ряд, такого же размера как основные KPI */}
            {kindCards.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 14, marginBottom: "var(--gap)",
              }}>
                {kindCards.map(({ kind, sum, count }) => {
                  const share = revenue > 0 ? (sum / revenue) * 100 : 0;
                  const color = KIND_COLORS[kind];
                  return (
                    <div key={kind} className="kpi" style={{
                      borderLeft: `4px solid ${color}`,
                      paddingLeft: 20,
                      minWidth: 0,
                    }}>
                      <div className="kpi-label" style={{ color }}>
                        {KIND_LABELS[kind]}
                      </div>
                      <div className="kpi-value kpi-value-sm">
                        {inMln(sum)}<span className="kpi-unit">млн ₽</span>
                      </div>
                      <div className="kpi-foot">
                        <span style={{ fontSize: 13, color: "var(--mute)" }} title="Доля в выручке">
                          {revenue > 0 ? <><strong style={{ color: "var(--ink-1)" }}>{share.toFixed(1).replace(".", ",")}%</strong> от выручки</> : "—"}
                        </span>
                        <span style={{ fontSize: 12, color: "var(--mute)" }}>{count} опер.</span>
                      </div>
                      {revenue > 0 && (
                        <div style={{ height: 6, background: "var(--paper-2)", borderRadius: 3, overflow: "hidden", marginTop: 2 }}>
                          <div style={{
                            width: share > 0 ? `max(3px, ${Math.min(100, share).toFixed(2)}%)` : "0",
                            height: "100%", background: color, transition: "width 0.3s",
                            borderRadius: 3,
                          }}/>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      })()}

      {/* ОБЩЕЕ САЛЬДО — широкая плашка прямо над таблицей. */}
      {(() => {
        const inc = data.totals.incomes_total;
        const exp = data.totals.expenses_total;
        const sal = inc - exp;
        return (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 18px", marginBottom: "var(--gap)",
            background: "var(--paper-2)", border: "1px solid var(--line)", borderRadius: 8,
            gap: 16, flexWrap: "wrap",
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--mute)" }}>
              Общее сальдо
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 24, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "var(--mute)" }}>
                Приход: <strong style={{ color: "var(--green)", fontVariantNumeric: "tabular-nums", fontSize: 16 }}>{formatRub(inc, { mln: false })}</strong>
              </span>
              <span style={{ fontSize: 13, color: "var(--mute)" }}>
                Расход: <strong style={{ color: "var(--brick)", fontVariantNumeric: "tabular-nums", fontSize: 16 }}>{formatRub(exp, { mln: false })}</strong>
              </span>
              <span style={{
                fontVariantNumeric: "tabular-nums", fontSize: 22, fontWeight: 700,
                color: sal >= 0 ? "var(--green)" : "var(--brick)",
              }}>
                {sal >= 0 ? "+" : ""}{formatRub(sal, { mln: false })}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Одна общая таблица: приход | разделитель | расход. Месяцы выровнены. */}
      <div className="card" style={{ minWidth: 0, padding: 0 }}>
        <CombinedTxTable incomes={data.incomes} expenses={data.expenses}
                         incTotal={data.totals.incomes_total} incCount={data.totals.incomes_count}
                         expTotal={data.totals.expenses_total} expCount={data.totals.expenses_count}
                         allCategories={categoriesQ.data || []}
                         allObjects={objectsQ.data || []}
                         toolbarFilters={
                           <>
                             <ObjectSearchPicker value={objectId} objects={objects}
                                                 onChange={setObjectId}/>
                             <select className="input-sm" value={responsibleId}
                                     onChange={e => setResponsibleId(e.target.value)}
                                     style={{ flex: "0 1 160px", minWidth: 130 }}>
                               <option value="">Любой ответств.</option>
                               {responsibles.map(r => (
                                 <option key={r.id} value={r.id}>{r.name}</option>
                               ))}
                             </select>
                           </>
                         }/>
      </div>
    </FinanceLayout>
  );
}


/** Общая таблица «приходы | расходы» с группировкой по месяцам.
 *  В каждом месяце строка-заголовок + N рядов = max(приходов, расходов).
 *  Заголовки колонок кликабельны — сортировка по столбцу. */
type SortKey = "date" | "object" | "what" | "responsible" | "amount";
type SortDir = "asc" | "desc";

function CombinedTxTable({ incomes, expenses, incTotal, incCount, expTotal, expCount, allCategories, allObjects, toolbarFilters }: {
  incomes: TransactionRow[];
  expenses: TransactionRow[];
  incTotal: number;
  incCount: number;
  expTotal: number;
  expCount: number;
  allCategories: ExpenseCategoryRow[];
  allObjects: ObjectRow[];
  toolbarFilters?: React.ReactNode;
}) {
  // Карта object_id → production_project_id для построения ссылок
  const ppIdByObjectId = new Map<number, number>();
  for (const o of allObjects) {
    if (o.production_project_id) ppIdByObjectId.set(o.id, o.production_project_id);
  }
  const renderObjectLink = (objectId: number | null, objectName: string | null) => {
    if (!objectName) return <span className="muted">—</span>;
    const ppId = objectId ? ppIdByObjectId.get(objectId) : undefined;
    if (ppId) {
      return (
        <Link to={"/production/$projectId" as "/"}
              params={{ projectId: String(ppId) } as never}
              onClick={(e) => e.stopPropagation()}
              title="Открыть карточку в Производстве"
              style={{ color: "var(--brick)", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span>{objectName}</span>
          <Icon name="external" size={11}/>
        </Link>
      );
    }
    return <span>{objectName}</span>;
  };
  void renderObjectLink;
  const qc = useQueryClient();
  const updMut = useMutation({
    mutationFn: (args: { id: number; category_id: number }) =>
      updateExpense(args.id, { category_id: args.category_id }),
    onSuccess: () => {
      invalAllFinance(qc);
    },
  });
  const revMut = useMutation({
    mutationFn: (args: { id: number; patch: Partial<RevenuePayload> }) =>
      updateRevenue(args.id, args.patch),
    onSuccess: () => {
      invalAllFinance(qc);
    },
    onError: (e: Error) => alert("Ошибка сохранения: " + e.message),
  });
  // Для каждого kind берём первую категорию (для смены через select)
  const firstCatByKind: Partial<Record<CategoryKind, ExpenseCategoryRow>> = {};
  for (const c of allCategories) {
    if (c.archived) continue;
    const k = c.kind as CategoryKind;
    if (!firstCatByKind[k]) firstCatByKind[k] = c;
  }
  const KIND_OPTIONS: CategoryKind[] = ["fot", "materials", "outsource", "taxes", "marketing", "sales", "other"];
  void KIND_OPTIONS;
  function changeKind(expenseId: number, newKind: CategoryKind) {
    const cat = firstCatByKind[newKind];
    if (cat) updMut.mutate({ id: expenseId, category_id: cat.id });
  }
  void changeKind;
  // Сортировка отдельно для прихода и расхода
  const [incSort, setIncSort] = useState<{ k: SortKey; d: SortDir }>({ k: "date", d: "desc" });
  const [expSort, setExpSort] = useState<{ k: SortKey; d: SortDir }>({ k: "date", d: "desc" });

  function toggleSort(setter: typeof setIncSort, current: { k: SortKey; d: SortDir }, k: SortKey) {
    if (current.k === k) setter({ k, d: current.d === "asc" ? "desc" : "asc" });
    else setter({ k, d: "asc" });
  }

  function sortRows(rows: TransactionRow[], kind: "income" | "expense", sort: { k: SortKey; d: SortDir }): TransactionRow[] {
    const cmp = (a: TransactionRow, b: TransactionRow): number => {
      let av: string | number = "", bv: string | number = "";
      switch (sort.k) {
        case "date":
          av = (kind === "income" ? a.received_at : a.date) || ""; bv = (kind === "income" ? b.received_at : b.date) || ""; break;
        case "object":
          av = (a.object_name || "").toLowerCase(); bv = (b.object_name || "").toLowerCase(); break;
        case "what":
          av = ((kind === "income" ? a.counterparty : a.category_name) || "").toLowerCase();
          bv = ((kind === "income" ? b.counterparty : b.category_name) || "").toLowerCase();
          break;
        case "responsible":
          av = (a.created_by_name || "").toLowerCase(); bv = (b.created_by_name || "").toLowerCase(); break;
        case "amount":
          av = a.amount; bv = b.amount; break;
      }
      if (av < bv) return sort.d === "asc" ? -1 : 1;
      if (av > bv) return sort.d === "asc" ? 1 : -1;
      return 0;
    };
    return [...rows].sort(cmp);
  }

  const sortedIncomes = sortRows(incomes, "income", incSort);
  const sortedExpenses = sortRows(expenses, "expense", expSort);

  const groupBy = (rows: TransactionRow[], kind: "income" | "expense") => {
    const map = new Map<string, TransactionRow[]>();
    for (const r of rows) {
      const raw = kind === "income" ? r.received_at : r.date;
      if (!raw || raw.length < 7) continue;
      const ym = raw.slice(0, 7);
      if (!map.has(ym)) map.set(ym, []);
      map.get(ym)!.push(r);
    }
    return map;
  };
  const incByMonth = groupBy(sortedIncomes, "income");
  const expByMonth = groupBy(sortedExpenses, "expense");
  const months = Array.from(new Set([...incByMonth.keys(), ...expByMonth.keys()])).sort().reverse();

  const { widths, startResize, resetWidths } = useDdsColumnWidths();
  const { visible, toggle: toggleVisible, reset: resetVisible } = useDdsVisibleCols();
  const [dateFmt, setDateFmt] = useDateFormat();
  const [topCreate, setTopCreate] = useState<"income" | "expense" | null>(null);
  // Редактирование строк теперь через модалки (а не inline-формы)
  const [editingIncome, setEditingIncome] = useState<TransactionRow | null>(null);
  const [editingExpense, setEditingExpense] = useState<TransactionRow | null>(null);
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  // Видимые ключи в порядке DDS_COL_KEYS, разделённые на income / expense
  const incCols = (["inc_date","inc_object","inc_manager","inc_what","inc_pay","inc_resp","inc_amount"] as DdsColKey[]).filter(k => visible.has(k));
  const expCols = (["exp_date","exp_object","exp_what","exp_pay","exp_kind","exp_resp","exp_amount"] as DdsColKey[]).filter(k => visible.has(k));
  const visKeys = [...incCols, ...expCols];
  const incColCount = incCols.length;
  const expColCount = expCols.length;
  const isVisible = (k: DdsColKey) => visible.has(k);

  // Сортирующий заголовок с resize-хэндлом
  const Sortable = ({ k, label, sort, onClick, isNum, colKey }: {
    k: SortKey; label: string;
    sort: { k: SortKey; d: SortDir };
    onClick: () => void;
    isNum?: boolean;
    colKey: DdsColKey;
  }) => (
    <th
      onClick={onClick}
      className={isNum ? "num" : ""}
      style={{ cursor: "pointer", userSelect: "none", position: "relative" }}
      title="Кликнуть — сортировать. Потянуть правую границу — изменить ширину."
    >
      {label}
      {sort.k === k && (
        <span style={{ marginLeft: 4, opacity: 0.6 }}>{sort.d === "asc" ? "▲" : "▼"}</span>
      )}
      <ResizeHandle onMouseDown={(e) => startResize(colKey, e)}/>
    </th>
  );

  if (months.length === 0) {
    return <div className="muted" style={{ padding: 24, textAlign: "center" }}>Операций нет</div>;
  }

  // Пустые income/expense ячейки: клик → открывает модалку создания (квадратную карточку).
  // Раньше тут разворачивалась inline-форма — теперь единый стиль с топ-кнопками «+ Приход/Расход».
  const EmptyIncomeCells = ({ ym: _ym }: { ym: string }) => (
    <td colSpan={Math.max(1, incColCount)}
        onClick={(e) => { e.stopPropagation(); setTopCreate("income"); }}
        style={{
          cursor: "pointer", borderRight: "2px solid var(--line)",
          padding: "8px 14px", color: "var(--mute)", fontSize: 12,
        }}
        title="Добавить приход">
      + добавить
    </td>
  );
  const EmptyExpenseCells = ({ ym: _ym }: { ym: string }) => (
    <td colSpan={Math.max(1, expColCount)}
        onClick={(e) => { e.stopPropagation(); setTopCreate("expense"); }}
        style={{
          cursor: "pointer",
          padding: "8px 14px", color: "var(--mute)", fontSize: 12,
        }}
        title="Добавить расход">
      + добавить
    </td>
  );

  // Ссылки Amo/Производство по object_id (для иконок в ячейке «Объект»)
  const objAmoById = new Map<number, string | null>();
  for (const o of allObjects) objAmoById.set(o.id, o.amo_url);

  const renderIncomeCells = (r: TransactionRow) => (
    <IncomeCells
      r={r}
      bg={monthBgColor(r)}
      dateText={fmtIsoDate(r.kind === "income" ? r.received_at : r.date, dateFmt)}
      onEdit={() => setEditingIncome(r)}
      saving={revMut.isPending}
      isVisible={isVisible}
      objAmoUrl={r.object_id ? objAmoById.get(r.object_id) : null}
      objPpId={r.object_id ? ppIdByObjectId.get(r.object_id) : null}
    />
  );

  // Полная мутация expense (date / object_id / description / amount / payment_method)
  const expFieldMut = useMutation({
    mutationFn: (args: { id: number; patch: Partial<{ date: string; object_id: number | null; description: string | null; amount: number; payment_method: PaymentMethod }> }) =>
      updateExpense(args.id, args.patch),
    onSuccess: () => { invalAllFinance(qc); },
    onError: (e: Error) => alert("Ошибка сохранения: " + e.message),
  });

  const renderExpenseCells = (r: TransactionRow) => (
    <ExpenseCells
      r={r}
      bg={monthBgColor(r)}
      dateText={fmtIsoDate(r.kind === "income" ? r.received_at : r.date, dateFmt)}
      onEdit={() => setEditingExpense(r)}
      saving={expFieldMut.isPending || updMut.isPending}
      isVisible={isVisible}
      objAmoUrl={r.object_id ? objAmoById.get(r.object_id) : null}
      objPpId={r.object_id ? ppIdByObjectId.get(r.object_id) : null}
    />
  );

  return (
    <div className="tbl-scroll" style={{ overflow: "visible" }}>
      <div style={{ display: "flex", padding: "10px 14px", gap: 6, position: "relative", alignItems: "center", flexWrap: "wrap" }}>
        {toolbarFilters}
        <button className="btn btn-sm" onClick={() => setTopCreate("income")}
                style={{ color: "var(--green)", borderColor: "var(--green)" }}
                title="Создать новый приход">
          + Приход
        </button>
        <button className="btn btn-sm" onClick={() => setTopCreate("expense")}
                style={{ color: "var(--brick)", borderColor: "var(--brick)" }}
                title="Создать новый расход">
          + Расход
        </button>
        <span style={{ flex: 1 }}/>
        <select
          className="input-sm"
          value={dateFmt}
          onChange={(e) => setDateFmt(e.target.value as DateFmt)}
          title="Формат отображения даты"
          style={{ flex: "0 0 auto", minWidth: 150 }}
        >
          <option value="short">Цифровой формат дат</option>
          <option value="long">Текстовый формат дат</option>
        </select>
        <div style={{ position: "relative" }}>
          <button className="btn btn-sm btn-ghost"
                  onClick={() => setColsMenuOpen(o => !o)}
                  title="Скрыть/показать колонки">
            <Icon name="filter" size={11}/> Колонки {visible.size < DDS_COL_KEYS.length && <span style={{ color: "var(--brick)", fontWeight: 700 }}>({visible.size}/{DDS_COL_KEYS.length})</span>}
          </button>
          {colsMenuOpen && (
            <>
              <div onClick={() => setColsMenuOpen(false)}
                   style={{ position: "fixed", inset: 0, zIndex: 90 }}/>
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", right: 0,
                background: "var(--card)", border: "1px solid var(--line)",
                borderRadius: 6, padding: 8, zIndex: 100,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                minWidth: 240, maxHeight: "70vh", overflowY: "auto",
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, padding: "4px 6px 8px" }}>
                  Видимые колонки
                </div>
                {DDS_COL_KEYS.map(k => (
                  <label key={k} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "5px 6px", cursor: "pointer", borderRadius: 4,
                    fontSize: 12,
                  }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = "transparent"}
                  >
                    <input type="checkbox"
                           checked={visible.has(k)}
                           onChange={() => toggleVisible(k)}/>
                    <span style={{ color: visible.has(k) ? "var(--ink-1)" : "var(--mute)" }}>
                      {DDS_COL_LABELS[k]}
                    </span>
                  </label>
                ))}
                <div style={{ borderTop: "1px solid var(--line)", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "flex-end" }}>
                  <button className="btn btn-sm btn-ghost" onClick={resetVisible}>Показать все</button>
                </div>
              </div>
            </>
          )}
        </div>
        <button className="btn btn-sm btn-ghost" onClick={resetWidths} title="Сбросить ширину колонок">
          <Icon name="refresh" size={11}/> Сброс ширин
        </button>
      </div>
      <table className="tbl tbl-tight dds-tbl" style={{
        fontSize: 12, tableLayout: "fixed",
        width: "100%",
        minWidth: visKeys.reduce((s, k) => s + widths[k], 0),
        borderTop: "1px solid var(--line)",
      }}>
        <colgroup>
          {visKeys.map(k => <col key={k} style={{ width: `${widths[k]}px` }}/>)}
        </colgroup>
        <thead>
          <tr>
            {incColCount > 0 && (
              <th colSpan={incColCount} style={{ color: "var(--green)", fontSize: 13, textTransform: "none", letterSpacing: 0, fontWeight: 700, borderRight: "2px solid var(--line)" }}>
                Приходы · {incCount}
              </th>
            )}
            {expColCount > 0 && (
              <th colSpan={expColCount} style={{ color: "var(--brick)", fontSize: 13, textTransform: "none", letterSpacing: 0, fontWeight: 700 }}>
                Расходы · {expCount}
              </th>
            )}
          </tr>
          <tr>
            {isVisible("inc_date") && <Sortable colKey="inc_date"   k="date"        label="Дата"       sort={incSort} onClick={() => toggleSort(setIncSort, incSort, "date")}/>}
            {isVisible("inc_object") && <Sortable colKey="inc_object" k="object"      label="Объект"     sort={incSort} onClick={() => toggleSort(setIncSort, incSort, "object")}/>}
            {isVisible("inc_manager") && <th style={{ position: "relative" }} title="Ответственный менеджер по сделке (из AmoCRM)">Менеджер<ResizeHandle onMouseDown={(e) => startResize("inc_manager", e)}/></th>}
            {isVisible("inc_what") && <Sortable colKey="inc_what"   k="what"        label="За что"     sort={incSort} onClick={() => toggleSort(setIncSort, incSort, "what")}/>}
            {isVisible("inc_pay") && <th style={{ position: "relative" }}>Оплата<ResizeHandle onMouseDown={(e) => startResize("inc_pay", e)}/></th>}
            {isVisible("inc_resp") && <Sortable colKey="inc_resp"   k="responsible" label="Ответств."  sort={incSort} onClick={() => toggleSort(setIncSort, incSort, "responsible")}/>}
            {isVisible("inc_amount") && <Sortable colKey="inc_amount" k="amount"      label="Сумма"      sort={incSort} onClick={() => toggleSort(setIncSort, incSort, "amount")} isNum/>}
            {isVisible("exp_date") && <Sortable colKey="exp_date"   k="date"        label="Дата"       sort={expSort} onClick={() => toggleSort(setExpSort, expSort, "date")}/>}
            {isVisible("exp_object") && <Sortable colKey="exp_object" k="object"      label="Объект"     sort={expSort} onClick={() => toggleSort(setExpSort, expSort, "object")}/>}
            {isVisible("exp_what") && <Sortable colKey="exp_what"   k="what"        label="Статья"     sort={expSort} onClick={() => toggleSort(setExpSort, expSort, "what")}/>}
            {isVisible("exp_pay") && <th style={{ position: "relative" }}>Оплата<ResizeHandle onMouseDown={(e) => startResize("exp_pay", e)}/></th>}
            {isVisible("exp_kind") && <th style={{ position: "relative" }}>Категория<ResizeHandle onMouseDown={(e) => startResize("exp_kind", e)}/></th>}
            {isVisible("exp_resp") && <Sortable colKey="exp_resp"   k="responsible" label="Ответств."  sort={expSort} onClick={() => toggleSort(setExpSort, expSort, "responsible")}/>}
            {isVisible("exp_amount") && <Sortable colKey="exp_amount" k="amount"      label="Сумма"      sort={expSort} onClick={() => toggleSort(setExpSort, expSort, "amount")} isNum/>}
          </tr>
        </thead>
        <tbody>
          {months.map(ym => {
            const incs = incByMonth.get(ym) || [];
            const exps = expByMonth.get(ym) || [];
            const incSum = incs.reduce((s, r) => s + r.amount, 0);
            const expSum = exps.reduce((s, r) => s + r.amount, 0);
            const balance = incSum - expSum;
            const rowsN = Math.max(incs.length, exps.length);
            const [y, m] = ym.split("-");
            const monthLabel = `${RU_MONTH_TX[Number(m) - 1]} ${y}`;
            const bg = MONTH_COLORS[m];
            return (
              <Fragment key={ym}>
                {/* Заголовок месяца — единая строка: МЕСЯЦ · Приход · Расход · Сальдо */}
                <tr style={{ background: bg }}>
                  <td colSpan={visKeys.length} style={{ padding: "10px 16px" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 24, flexWrap: "wrap" }}>
                      <strong style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 110 }}>
                        {monthLabel}
                      </strong>
                      <span style={{ fontSize: 13, color: "var(--mute)" }}>
                        Общий приход: <strong style={{ color: "var(--green)", fontVariantNumeric: "tabular-nums", fontSize: 16 }}>{formatRub(incSum, { mln: false })}</strong>
                      </span>
                      <span style={{ fontSize: 13, color: "var(--mute)" }}>
                        Общий расход: <strong style={{ color: "var(--brick)", fontVariantNumeric: "tabular-nums", fontSize: 16 }}>{formatRub(expSum, { mln: false })}</strong>
                      </span>
                      <span style={{ fontSize: 13, color: "var(--mute)" }}>
                        Сальдо месяца: <strong style={{ color: balance >= 0 ? "var(--green)" : "var(--brick)", fontVariantNumeric: "tabular-nums", fontSize: 16 }}>
                          {balance >= 0 ? "+" : ""}{formatRub(balance, { mln: false })}
                        </strong>
                      </span>
                    </div>
                  </td>
                </tr>
                {/* Строки операций — синхронизированы по индексу */}
                {Array.from({ length: rowsN }).map((_, i) => (
                  <tr key={`${ym}-${i}`}>
                    {incs[i] ? renderIncomeCells(incs[i]) : <EmptyIncomeCells ym={ym}/>}
                    {exps[i] ? renderExpenseCells(exps[i]) : <EmptyExpenseCells ym={ym}/>}
                  </tr>
                ))}
              </Fragment>
            );
          })}
          {/* Общий итог */}
          <tr style={{ borderTop: "2px solid var(--line)", fontWeight: 700, background: "var(--paper-2)", fontSize: 15 }}>
            {isVisible("inc_amount") ? (
              <>
                <td colSpan={Math.max(1, incColCount - 1)} style={{ padding: "12px 14px", textTransform: "uppercase", letterSpacing: 0.4 }}>Итого приход</td>
                <td className="num tabular" style={{ color: "var(--green)", borderRight: "2px solid var(--line)", fontSize: 17, padding: "12px 14px" }}>{formatRub(incTotal, { mln: false })}</td>
              </>
            ) : incColCount > 0 && (
              <td colSpan={incColCount} style={{ borderRight: "2px solid var(--line)", padding: "12px 14px" }}>Итого приход: <span style={{ color: "var(--green)", fontSize: 17 }}>{formatRub(incTotal, { mln: false })}</span></td>
            )}
            {isVisible("exp_amount") ? (
              <>
                <td colSpan={Math.max(1, expColCount - 1)} style={{ padding: "12px 14px", textTransform: "uppercase", letterSpacing: 0.4 }}>Итого расход</td>
                <td className="num tabular" style={{ color: "var(--brick)", fontSize: 17, padding: "12px 14px" }}>{formatRub(expTotal, { mln: false })}</td>
              </>
            ) : expColCount > 0 && (
              <td colSpan={expColCount} style={{ padding: "12px 14px" }}>Итого расход: <span style={{ color: "var(--brick)", fontSize: 17 }}>{formatRub(expTotal, { mln: false })}</span></td>
            )}
          </tr>
        </tbody>
      </table>
      {/* Создание прихода / расхода — обе модалки одной формы (квадратная карточка). */}
      <RevenueFormModal
        open={topCreate === "income"}
        initial={null}
        onClose={() => setTopCreate(null)}
      />
      {topCreate === "expense" && (
        <ExpenseFormModal
          open
          initial={null}
          categories={allCategories}
          onClose={() => setTopCreate(null)}
          onSaved={() => {
            invalAllFinance(qc);
            void qc.invalidateQueries({ queryKey: ["finance-expenses"] });
            void qc.invalidateQueries({ queryKey: ["finance-overview"] });
            setTopCreate(null);
          }}
        />
      )}
      {/* Редактирование строк — те же модалки с initial. Открываются по клику на любую ячейку. */}
      <RevenueFormModal
        open={!!editingIncome}
        initial={editingIncome ? txToRevenueRow(editingIncome) : null}
        onClose={() => setEditingIncome(null)}
      />
      {editingIncome === null ? null : null /* placeholder, RevenueFormModal above handles edit */}
      {editingExpense && (
        <ExpenseFormModal
          open
          initial={txToExpenseRow(editingExpense)}
          categories={allCategories}
          onClose={() => setEditingExpense(null)}
          onSaved={() => {
            invalAllFinance(qc);
            void qc.invalidateQueries({ queryKey: ["finance-expenses"] });
            void qc.invalidateQueries({ queryKey: ["finance-overview"] });
            setEditingExpense(null);
          }}
        />
      )}
    </div>
  );
}


/** Excel-импорт расходов всем ставит date = 15 число месяца (синтетика).
 *  Для таких расходов показываем «Окт 2025» вместо «2025-10-15». */
const RU_MONTH_TX = ["Янв","Фев","Мар","Апр","Май","Июн","Июл","Авг","Сен","Окт","Ноя","Дек"];
const RU_MONTH_LONG = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];

type DateFmt = "short" | "long";
const DATE_FMT_KEY = "kb-finance-dds-datefmt";
function useDateFormat(): [DateFmt, (f: DateFmt) => void] {
  const [fmt, setFmt] = useState<DateFmt>(() => {
    try { const v = localStorage.getItem(DATE_FMT_KEY); return (v === "short" || v === "long") ? v : "short"; } catch { return "short"; }
  });
  useEffect(() => { try { localStorage.setItem(DATE_FMT_KEY, fmt); } catch { /* ignore */ } }, [fmt]);
  return [fmt, setFmt];
}

function fmtIsoDate(raw: string | null | undefined, fmt: DateFmt): string {
  if (!raw) return "—";
  const [y, m, d] = raw.split("-");
  if (!y || !m || !d) return raw;
  if (fmt === "short") return `${d}.${m}.${y}`;
  return `${parseInt(d, 10)} ${RU_MONTH_LONG[Number(m) - 1]} ${y} года`;
}

/** Лeгacy-обёртка: формат «D месяца YYYY» (используется в callsites где fmt не передан). */
function fmtTxDate(r: TransactionRow): string {
  const raw = r.kind === "income" ? r.received_at : r.date;
  return fmtIsoDate(raw, "long");
}

/** Кастомный picker категории расхода — цветной бейдж + popup-меню с цветными вариантами. */
function CategoryPicker({ value, options, onChange, disabled }: {
  value: CategoryKind;
  options: CategoryKind[];
  onChange: (k: CategoryKind) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative", display: "block", width: "100%", minWidth: 0 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        disabled={disabled}
        style={{
          background: KIND_COLORS[value],
          color: "#fff", border: "none", borderRadius: 14,
          padding: "6px 12px", fontSize: 11.5, fontWeight: 600,
          cursor: disabled ? "wait" : "pointer",
          display: "flex", alignItems: "center", gap: 6,
          opacity: disabled ? 0.6 : 1,
          width: "100%", maxWidth: "100%", overflow: "hidden",
          textAlign: "left", justifyContent: "space-between",
        }}
        title="Изменить категорию"
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {KIND_LABELS[value]}
        </span>
        <span style={{ fontSize: 9, opacity: 0.85, lineHeight: 1, flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <>
          {/* Невидимый overlay для закрытия по клику вне */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 100 }}
          />
          <div
            style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0,
              background: "var(--card)", border: "1px solid var(--line)",
              borderRadius: 6, padding: 4, zIndex: 101,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              display: "flex", flexDirection: "column", gap: 2,
              minWidth: 110,
            }}
          >
            {options.map(k => (
              <button
                key={k}
                type="button"
                onClick={() => { onChange(k); setOpen(false); }}
                style={{
                  background: k === value ? KIND_COLORS[k] : "transparent",
                  color: k === value ? "#fff" : "var(--ink-2)",
                  border: "none", padding: "5px 10px",
                  fontSize: 12, fontWeight: 500,
                  cursor: "pointer", textAlign: "left",
                  borderRadius: 4,
                  display: "flex", alignItems: "center", gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (k !== value) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)";
                }}
                onMouseLeave={(e) => {
                  if (k !== value) (e.currentTarget as HTMLElement).style.background = "transparent";
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: KIND_COLORS[k] }}/>
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


/* ── Inline-edit ячейки приходов в ДДС ─────────────────────────── */

function IncomeCells({ r, bg, dateText, onEdit, saving, isVisible, objAmoUrl, objPpId }: {
  r: TransactionRow;
  bg: string | undefined;
  dateText: string;
  onEdit: () => void;
  saving: boolean;
  isVisible: (k: DdsColKey) => boolean;
  objAmoUrl?: string | null;
  objPpId?: number | null;
}) {
  const click = (e: React.MouseEvent) => { e.stopPropagation(); if (!saving) onEdit(); };
  const cellHover: React.CSSProperties = { cursor: "pointer" };
  // Граница «income | expense» — на последней видимой income-колонке
  const incOrder: DdsColKey[] = ["inc_date","inc_object","inc_manager","inc_what","inc_pay","inc_resp","inc_amount"];
  const lastVis = [...incOrder].reverse().find(isVisible);
  const sep = (k: DdsColKey): React.CSSProperties => k === lastVis ? { borderRight: "2px solid var(--line)" } : {};
  return (
    <>
      {isVisible("inc_date") && <td onClick={click} className="tabular" style={{ background: bg, fontWeight: 500, ...cellHover, ...sep("inc_date") }}>{dateText}</td>}
      {isVisible("inc_object") && <td onClick={click} style={{ ...cellHover, ...sep("inc_object") }}>
        {r.object_name ? (
          <span style={{ fontWeight: 600, color: "var(--brick)" }}>
            {r.object_name}
            <ObjLinkIcons amoUrl={objAmoUrl} ppId={objPpId}/>
          </span>
        ) : <span style={{ color: "var(--mute)" }}>—</span>}
        {r.region && <div style={{ fontSize: 10, color: "var(--mute)" }}>{r.region}</div>}
      </td>}
      {isVisible("inc_manager") && <td onClick={click} style={{ ...cellHover, ...sep("inc_manager") }} title={r.sales_manager_name || "Менеджер не назначен в AmoCRM"}>
        {r.sales_manager_name ? (
          <span style={{ fontSize: 12, color: "var(--ink-1)" }}>{r.sales_manager_name}</span>
        ) : <span style={{ color: "var(--mute)" }}>—</span>}
      </td>}
      {isVisible("inc_what") && <td onClick={click} style={{ ...cellHover, ...sep("inc_what") }}>
        <strong>{r.counterparty || <span className="muted">—</span>}</strong>
      </td>}
      {isVisible("inc_pay") && <td onClick={click} style={{ padding: "4px 8px", ...cellHover, ...sep("inc_pay") }}>
        <span style={{
          display: "inline-block", padding: "3px 9px", borderRadius: 12,
          fontSize: 11, fontWeight: 600, color: "#fff",
          background: r.payment_method === "cash" ? "#3D7A3D"
                    : r.payment_method === "card_transfer" ? "#2E5BA0"
                    : "#5d4ec1",
        }}>
          {r.payment_method === "cash" ? "Нал" : r.payment_method === "card_transfer" ? "Карта" : "Безнал"}
        </span>
      </td>}
      {isVisible("inc_resp") && <td onClick={click} style={{ padding: "4px 8px", ...cellHover, ...sep("inc_resp") }}>
        {r.responsible_user_name ? (
          <span style={{
            display: "inline-block", padding: "3px 9px", borderRadius: 12,
            fontSize: 11, fontWeight: 600, color: "#fff",
            background: r.responsible_kind ? RESPONSIBLE_KIND_COLORS[r.responsible_kind] : "var(--mute)",
          }}>
            {r.responsible_user_name}
          </span>
        ) : <span style={{ color: "var(--mute)" }}>—</span>}
      </td>}
      {isVisible("inc_amount") && <td onClick={click} className="num strong tabular" style={{ color: "var(--green)", ...cellHover, ...sep("inc_amount") }}>
        {formatRub(r.amount, { mln: false })}
      </td>}
    </>
  );
}

/** Overlay-редактор существующего прихода. Тот же стиль что и CreateIncomeCells. */
void IncomeRowEditor;
function IncomeRowEditor({ r, allObjects, ppIdByObjectId, onClose, colSpan }: {
  r: TransactionRow;
  allObjects: ObjectRow[];
  ppIdByObjectId: Map<number, number>;
  onClose: () => void;
  colSpan: number;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    received_at: r.received_at || "",
    object_id: r.object_id,
    counterparty: r.counterparty || "",
    payment_method: r.payment_method,
    responsible_kind: r.responsible_kind || null as ResponsibleKind | null,
    responsible_user_id: r.responsible_user_id || null as string | null,
    amount: r.amount,
  });
  const updMut = useMutation({
    mutationFn: (patch: Partial<RevenuePayload>) => updateRevenue(r.id, patch),
    onSuccess: () => { invalAllFinance(qc); onClose(); },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const delMut = useMutation({
    mutationFn: async () => {
      try { await deleteRevenue(r.id); }
      catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          if (confirm(`${e.message}\n\nВсё равно удалить?`)) {
            await deleteRevenue(r.id, { force: true });
          } else { throw new Error("Удаление отменено"); }
        } else { throw e; }
      }
    },
    onSuccess: () => { invalAllFinance(qc); onClose(); },
    onError: (e: Error) => { if (e.message !== "Удаление отменено") alert("Ошибка удаления: " + e.message); },
  });
  function save() {
    if (updMut.isPending || !draft.amount || draft.amount <= 0) return;
    updMut.mutate({
      received_at: draft.received_at,
      object_id: draft.object_id,
      counterparty: draft.counterparty || null,
      payment_method: draft.payment_method,
      responsible_kind: draft.responsible_kind,
      responsible_user_id: draft.responsible_user_id,
      amount: draft.amount,
    });
  }
  function handleDelete() {
    if (delMut.isPending) return;
    if (!confirm(`Удалить приход #${r.id} (${r.counterparty || "без названия"}, ${formatRub(r.amount)})?`)) return;
    delMut.mutate();
  }
  return (
    <td colSpan={colSpan || 1} style={{ padding: 0, borderRight: "2px solid var(--line)", position: "relative", overflow: "visible", height: 52 }}>
      <div style={{
        position: "absolute", top: 4, left: 6,
        width: "max-content", maxWidth: "calc(100vw - 40px)", minHeight: 44,
        background: "var(--card)", border: "1.5px solid var(--green)",
        borderRadius: 6, padding: "8px 12px",
        display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap",
        zIndex: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--green)", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
          Приход #{r.id}
        </span>
        <input type="date" className="input input-sm"
               value={draft.received_at}
               onChange={(e) => setDraft(d => ({ ...d, received_at: e.target.value }))}
               style={{ width: 130, fontSize: 12, flexShrink: 0 }}
               disabled={updMut.isPending}/>
        <div style={{ width: 180, flexShrink: 0 }}>
          <ObjectCell value={draft.object_id} name={
            draft.object_id ? (allObjects.find(o => o.id === draft.object_id)?.client_name ?? null) : null
          } options={allObjects} ppIdByObjectId={ppIdByObjectId}
                      onChange={(oid) => setDraft(d => ({ ...d, object_id: oid }))}
                      disabled={updMut.isPending}/>
        </div>
        <AutoGrowTextarea autoFocus
               className="input input-sm"
               placeholder="За что (контрагент)" value={draft.counterparty}
               onChange={(e) => setDraft(d => ({ ...d, counterparty: e.target.value }))}
               onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === "Escape") onClose(); }}
               style={{ flex: "1 1 160px", minWidth: 130, fontSize: 12, fontWeight: 600 }}
               disabled={updMut.isPending}/>
        <div style={{ width: 100, flexShrink: 0 }}>
          <PaymentMethodPicker value={draft.payment_method}
                               onChange={(pm) => setDraft(d => ({ ...d, payment_method: pm }))}
                               disabled={updMut.isPending}/>
        </div>
        <div style={{ width: 160, flexShrink: 0 }}>
          <ResponsiblePersonPicker valueUserId={draft.responsible_user_id}
                                   valueName={r.responsible_user_name ?? null}
                                   onChange={(uid, kind) => setDraft(d => ({ ...d, responsible_user_id: uid, responsible_kind: kind }))}
                                   disabled={updMut.isPending}/>
        </div>
        <input className="input input-sm" type="number"
               value={draft.amount || ""}
               onChange={(e) => setDraft(d => ({ ...d, amount: parseFloat(e.target.value) || 0 }))}
               onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }}
               style={{ width: 110, fontSize: 13, textAlign: "right", color: "var(--green)", fontWeight: 700, flexShrink: 0 }}
               disabled={updMut.isPending}/>
        <button type="button" className="btn btn-sm" onClick={handleDelete}
                disabled={delMut.isPending || updMut.isPending}
                style={{ flexShrink: 0, color: "var(--brick)", borderColor: "var(--brick)" }}
                title="Удалить приход">
          {delMut.isPending ? "…" : <Icon name="trash" size={12}/>}
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save}
                disabled={!draft.amount || updMut.isPending} style={{ flexShrink: 0 }}>
          {updMut.isPending ? "…" : "Сохранить"}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}
                disabled={updMut.isPending} style={{ flexShrink: 0 }} title="Отмена (Esc)">
          <Icon name="x" size={12}/>
        </button>
      </div>
    </td>
  );
}

function ExpenseCells({ r, bg, dateText, onEdit, saving, isVisible, objAmoUrl, objPpId }: {
  r: TransactionRow;
  bg: string | undefined;
  dateText: string;
  onEdit: () => void;
  saving: boolean;
  isVisible: (k: DdsColKey) => boolean;
  objAmoUrl?: string | null;
  objPpId?: number | null;
}) {
  const click = (e: React.MouseEvent) => { e.stopPropagation(); if (!saving) onEdit(); };
  const cellHover: React.CSSProperties = { cursor: "pointer" };
  return (
    <>
      {isVisible("exp_date") && <td onClick={click} className="tabular" style={{ background: bg, fontWeight: 500, ...cellHover }}>{dateText}</td>}
      {isVisible("exp_object") && <td onClick={click} style={cellHover}>
        {r.object_name ? (
          <span style={{ fontWeight: 600, color: "var(--brick)" }}>
            {r.object_name}
            <ObjLinkIcons amoUrl={objAmoUrl} ppId={objPpId}/>
          </span>
        ) : <span style={{ color: "var(--mute)" }}>—</span>}
        {r.region && <div style={{ fontSize: 10, color: "var(--mute)" }}>{r.region}</div>}
      </td>}
      {isVisible("exp_what") && <td onClick={click} style={cellHover}>
        <strong>{r.description || r.category_name || <span className="muted">—</span>}</strong>
      </td>}
      {isVisible("exp_pay") && <td onClick={click} style={{ padding: "4px 8px", ...cellHover }}>
        <span style={{
          display: "inline-block", padding: "3px 9px", borderRadius: 12,
          fontSize: 11, fontWeight: 600, color: "#fff",
          background: r.payment_method === "cash" ? "#3D7A3D"
                    : r.payment_method === "card_transfer" ? "#2E5BA0"
                    : "#5d4ec1",
        }}>
          {r.payment_method === "cash" ? "Нал" : r.payment_method === "card_transfer" ? "Карта" : "Безнал"}
        </span>
      </td>}
      {isVisible("exp_kind") && <td onClick={click} style={{ padding: "4px 8px", ...cellHover }}>
        {r.category_kind ? (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{
              display: "inline-block", padding: "3px 9px", borderRadius: 12,
              fontSize: 11, fontWeight: 600, color: "#fff",
              background: KIND_COLORS[r.category_kind],
            }}>
              {KIND_LABELS[r.category_kind]}
            </span>
            {r.secondary_category_kind && (
              <span title="Доп. категория"
                    style={{
                      display: "inline-block", padding: "2px 7px", borderRadius: 10,
                      fontSize: 10, fontWeight: 600, color: "#fff",
                      background: KIND_COLORS[r.secondary_category_kind], opacity: 0.75,
                    }}>
                +{KIND_LABELS[r.secondary_category_kind]}
              </span>
            )}
          </span>
        ) : <span style={{ color: "var(--mute)" }}>—</span>}
      </td>}
      {isVisible("exp_resp") && <td onClick={click} style={{ padding: "4px 8px", ...cellHover }}>
        {r.responsible_user_name ? (
          <span style={{
            display: "inline-block", padding: "3px 9px", borderRadius: 12,
            fontSize: 11, fontWeight: 600, color: "#fff",
            background: r.responsible_kind ? RESPONSIBLE_KIND_COLORS[r.responsible_kind] : "var(--mute)",
          }}>
            {r.responsible_user_name}
          </span>
        ) : <span style={{ color: "var(--mute)" }}>—</span>}
      </td>}
      {isVisible("exp_amount") && <td onClick={click} className="num strong tabular" style={{ color: "var(--brick)", ...cellHover }}>
        {formatRub(r.amount, { mln: false })}
      </td>}
    </>
  );
}

/** Overlay-редактор существующего расхода. */
void ExpenseRowEditor;
function ExpenseRowEditor({ r, allObjects, allCategories, ppIdByObjectId, onClose, colSpan }: {
  r: TransactionRow;
  allObjects: ObjectRow[];
  allCategories: ExpenseCategoryRow[];
  ppIdByObjectId: Map<number, number>;
  onClose: () => void;
  colSpan: number;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    date: r.date || "",
    object_id: r.object_id,
    description: r.description || "",
    payment_method: r.payment_method,
    kind: (r.category_kind || "other") as CategoryKind,
    secondary_kind: (r.secondary_category_kind || "") as CategoryKind | "",
    responsible_kind: (r.responsible_kind || null) as ResponsibleKind | null,
    responsible_user_id: (r.responsible_user_id || null) as string | null,
    amount: r.amount,
  });
  const firstCatByKind: Partial<Record<CategoryKind, ExpenseCategoryRow>> = {};
  for (const c of allCategories) { if (!c.archived && !firstCatByKind[c.kind as CategoryKind]) firstCatByKind[c.kind as CategoryKind] = c; }
  const KIND_OPTIONS: CategoryKind[] = ["fot", "materials", "outsource", "taxes", "marketing", "sales", "other"];
  const KIND_LABELS_LOCAL: Record<CategoryKind, string> = {
    fot: "ФОТ", materials: "Материалы", outsource: "Аутсорс", taxes: "Налоги",
    marketing: "Маркетинг", sales: "ОП", other: "Прочее",
  };

  const updMut = useMutation({
    mutationFn: (patch: Partial<ExpensePayload>) => updateExpense(r.id, patch),
    onSuccess: () => { invalAllFinance(qc); onClose(); },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const delMut = useMutation({
    mutationFn: async () => {
      try { await deleteExpense(r.id); }
      catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          if (confirm(`${e.message}\n\nВсё равно удалить?`)) {
            await deleteExpense(r.id, { force: true });
          } else { throw new Error("Удаление отменено"); }
        } else { throw e; }
      }
    },
    onSuccess: () => { invalAllFinance(qc); onClose(); },
    onError: (e: Error) => { if (e.message !== "Удаление отменено") alert("Ошибка удаления: " + e.message); },
  });
  function handleDelete() {
    if (delMut.isPending) return;
    if (!confirm(`Удалить расход #${r.id} (${r.description || r.category_name || "без названия"}, ${formatRub(r.amount)})?`)) return;
    delMut.mutate();
  }
  function save() {
    if (updMut.isPending || !draft.amount || draft.amount <= 0) return;
    const patch: Partial<ExpensePayload> = {
      date: draft.date,
      object_id: draft.object_id,
      description: draft.description || null,
      payment_method: draft.payment_method,
      responsible_kind: draft.responsible_kind,
      responsible_user_id: draft.responsible_user_id,
      amount: draft.amount,
    };
    // Если kind изменился — меняем category_id на первую категорию этого типа
    if (draft.kind !== r.category_kind) {
      const cat = firstCatByKind[draft.kind];
      if (cat) patch.category_id = cat.id;
    }
    // Вторая категория (тег): пусто = очистить; иначе — первая категория этого kind
    if (draft.secondary_kind !== (r.secondary_category_kind || "")) {
      if (draft.secondary_kind === "") {
        patch.secondary_category_id = null;
      } else {
        const cat2 = firstCatByKind[draft.secondary_kind];
        if (cat2) patch.secondary_category_id = cat2.id;
      }
    }
    updMut.mutate(patch);
  }
  return (
    <td colSpan={colSpan || 1} style={{ padding: 0, position: "relative", overflow: "visible", height: 52 }}>
      <div style={{
        position: "absolute", top: 4, right: 6,
        width: "max-content", maxWidth: "calc(100vw - 40px)", minHeight: 44,
        background: "var(--card)", border: "1.5px solid var(--brick)",
        borderRadius: 6, padding: "8px 12px",
        display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap",
        zIndex: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--brick)", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
          Расход #{r.id}
        </span>
        <input type="date" className="input input-sm"
               value={draft.date}
               onChange={(e) => setDraft(d => ({ ...d, date: e.target.value }))}
               style={{ width: 130, fontSize: 12, flexShrink: 0 }}
               disabled={updMut.isPending}/>
        <div style={{ width: 180, flexShrink: 0 }}>
          <ObjectCell value={draft.object_id} name={
            draft.object_id ? (allObjects.find(o => o.id === draft.object_id)?.client_name ?? null) : null
          } options={allObjects} ppIdByObjectId={ppIdByObjectId}
                      onChange={(oid) => setDraft(d => ({ ...d, object_id: oid }))}
                      disabled={updMut.isPending}/>
        </div>
        <AutoGrowTextarea autoFocus
               className="input input-sm"
               placeholder="Статья / описание" value={draft.description}
               onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
               onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === "Escape") onClose(); }}
               style={{ flex: "1 1 160px", minWidth: 130, fontSize: 12, fontWeight: 600 }}
               disabled={updMut.isPending}/>
        <div style={{ width: 100, flexShrink: 0 }}>
          <PaymentMethodPicker value={draft.payment_method}
                               onChange={(pm) => setDraft(d => ({ ...d, payment_method: pm }))}
                               disabled={updMut.isPending}/>
        </div>
        <div style={{ width: 130, flexShrink: 0 }}>
          <CategoryPicker value={draft.kind} options={KIND_OPTIONS}
                          onChange={(k) => setDraft(d => ({ ...d, kind: k }))}
                          disabled={updMut.isPending}/>
        </div>
        <select className="input input-sm"
                value={draft.secondary_kind}
                onChange={(e) => setDraft(d => ({ ...d, secondary_kind: e.target.value as CategoryKind | "" }))}
                disabled={updMut.isPending}
                title="Доп. категория (тег). Не влияет на итоги, но позволяет двойную классификацию."
                style={{ width: 110, fontSize: 11, flexShrink: 0, color: "var(--mute)" }}>
          <option value="">+ доп. (нет)</option>
          {KIND_OPTIONS.map(k => (
            <option key={k} value={k} disabled={k === draft.kind}>+ {KIND_LABELS_LOCAL[k]}</option>
          ))}
        </select>
        <div style={{ width: 160, flexShrink: 0 }}>
          <ResponsiblePersonPicker valueUserId={draft.responsible_user_id}
                                   valueName={r.responsible_user_name ?? null}
                                   onChange={(uid, kind) => setDraft(d => ({ ...d, responsible_user_id: uid, responsible_kind: kind }))}
                                   disabled={updMut.isPending}/>
        </div>
        <input className="input input-sm" type="number"
               value={draft.amount || ""}
               onChange={(e) => setDraft(d => ({ ...d, amount: parseFloat(e.target.value) || 0 }))}
               onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }}
               style={{ width: 110, fontSize: 13, textAlign: "right", color: "var(--brick)", fontWeight: 700, flexShrink: 0 }}
               disabled={updMut.isPending}/>
        <button type="button" className="btn btn-sm" onClick={handleDelete}
                disabled={delMut.isPending || updMut.isPending}
                style={{ flexShrink: 0, color: "var(--brick)", borderColor: "var(--brick)" }}
                title="Удалить расход">
          {delMut.isPending ? "…" : <Icon name="trash" size={12}/>}
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save}
                disabled={!draft.amount || updMut.isPending} style={{ flexShrink: 0 }}>
          {updMut.isPending ? "…" : "Сохранить"}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}
                disabled={updMut.isPending} style={{ flexShrink: 0 }} title="Отмена (Esc)">
          <Icon name="x" size={12}/>
        </button>
      </div>
    </td>
  );
}


function ObjectCell({ value, name, options, ppIdByObjectId, onChange, disabled }: {
  value: number | null;
  name: string | null;
  options: ObjectRow[];
  ppIdByObjectId: Map<number, number>;
  onChange: (id: number | null) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = options.filter(o =>
    !search || (o.client_name || "").toLowerCase().includes(search.toLowerCase()),
  );
  const ppId = value ? ppIdByObjectId.get(value) : undefined;
  return (
    <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(o => !o); }}
        disabled={disabled}
        style={{
          background: "transparent", border: "1px dashed transparent",
          padding: "2px 4px", margin: "-2px -4px",
          fontSize: 12, fontWeight: 600,
          cursor: disabled ? "wait" : "pointer",
          color: name ? (ppId ? "var(--brick)" : "var(--ink-1)") : "var(--mute)",
          textDecoration: ppId ? "underline" : "none",
          textAlign: name ? "left" : "center",
          width: "100%", display: "flex", alignItems: "center", gap: 4,
          justifyContent: name ? "flex-start" : "center",
        }}
        title={name ? "Изменить объект" : "Выбрать объект"}
        onMouseEnter={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.borderColor = "var(--line)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "transparent"; }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name || "— выбрать —"}
        </span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 100 }}/>
          <div style={{
            position: "absolute", top: "calc(100% + 2px)", left: 0,
            width: 280, maxHeight: 320, overflowY: "auto",
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 6, padding: 4, zIndex: 101,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          }}>
            <input
              autoFocus
              className="input input-sm"
              placeholder="Поиск объекта…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: "100%", marginBottom: 4 }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false); }}
              style={{
                width: "100%", textAlign: "left", padding: "5px 8px",
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 12, color: "var(--mute)", borderRadius: 4,
              }}
            >
              — без объекта —
            </button>
            {filtered.slice(0, 60).map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); }}
                style={{
                  width: "100%", textAlign: "left", padding: "5px 8px",
                  background: o.id === value ? "var(--paper-2)" : "transparent",
                  border: "none", cursor: "pointer",
                  fontSize: 12, color: "var(--ink-1)", borderRadius: 4,
                }}
                onMouseEnter={(e) => { if (o.id !== value) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
                onMouseLeave={(e) => { if (o.id !== value) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                {o.client_name}
                {o.region && <span style={{ fontSize: 10, color: "var(--mute)" }}> · {o.region}</span>}
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: "8px", fontSize: 11, color: "var(--mute)", textAlign: "center" }}>
                Ничего не найдено
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

void TextCell;
function TextCell({ value, placeholder, onSave, disabled, bold, suffix }: {
  value: string;
  placeholder?: string;
  onSave: (v: string) => void;
  disabled: boolean;
  bold?: boolean;
  suffix?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (editing) {
    return (
      <input
        autoFocus
        className="input input-sm"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); if (draft !== value) onSave(draft); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.currentTarget.blur(); }
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        style={{ width: "100%", fontSize: 12, fontWeight: bold ? 700 : 400 }}
        disabled={disabled}
      />
    );
  }
  return (
    <div
      onClick={(e) => { e.stopPropagation(); if (!disabled) setEditing(true); }}
      style={{
        cursor: disabled ? "default" : "text",
        display: "block", width: "100%", minHeight: 22,
        padding: "3px 6px", margin: "-3px -6px",
        borderRadius: 3, fontWeight: bold ? 700 : 400,
        color: "var(--ink-1)",
        transition: "background 0.1s",
      }}
      title="Кликните для редактирования"
      onMouseEnter={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
    >
      {value || placeholder || "—"}
      {suffix && <span style={{ color: "var(--mute)", fontWeight: 400 }}>{suffix}</span>}
    </div>
  );
}

void DateCell;
function DateCell({ value, onSave, disabled }: {
  value: string;  // ISO YYYY-MM-DD
  onSave: (v: string) => void;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const display = (() => {
    if (!value) return "—";
    const [y, m, d] = value.split("-");
    return `${d}.${m}.${y.slice(2)}`;
  })();
  if (editing) {
    return (
      <input
        autoFocus
        type="date"
        className="input input-sm"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); if (draft && draft !== value) onSave(draft); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        disabled={disabled}
        style={{ width: "100%", maxWidth: "100%", fontSize: 10, padding: "1px 2px", boxSizing: "border-box" }}
      />
    );
  }
  return (
    <div
      onClick={(e) => { e.stopPropagation(); if (!disabled) setEditing(true); }}
      style={{
        cursor: disabled ? "default" : "text",
        display: "block", width: "100%", minHeight: 22,
        padding: "3px 4px", boxSizing: "border-box",
        borderRadius: 3, transition: "background 0.1s",
        fontVariantNumeric: "tabular-nums",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}
      title="Кликните чтобы изменить дату"
      onMouseEnter={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
    >
      {display}
    </div>
  );
}

void AmountCell;
function AmountCell({ value, onSave, disabled }: {
  value: number;
  onSave: (v: number) => void;
  disabled: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  if (editing) {
    return (
      <input
        autoFocus
        className="input input-sm"
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const n = parseFloat(draft);
          if (!isNaN(n) && n !== value) onSave(n);
          else setDraft(String(value));
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.currentTarget.blur(); }
          if (e.key === "Escape") { setDraft(String(value)); setEditing(false); }
        }}
        style={{ width: "100%", fontSize: 12, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
        disabled={disabled}
      />
    );
  }
  return (
    <div
      onClick={(e) => { e.stopPropagation(); if (!disabled) setEditing(true); }}
      style={{
        cursor: disabled ? "default" : "text",
        display: "block", width: "100%", minHeight: 22,
        padding: "3px 6px", margin: "-3px -6px",
        borderRadius: 3, textAlign: "right",
        transition: "background 0.1s",
      }}
      title="Кликните для редактирования"
      onMouseEnter={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.background = "rgba(61,122,61,0.08)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
    >
      {formatRub(value)}
    </div>
  );
}

/**
 * Пустые income-ячейки превращаются в редактируемый черновой приход по клику.
 * Required для создания: amount > 0. Дата по умолчанию — 1-е число месяца строки.
 */
/**
 * Модальное окно «+ Приход» / «+ Расход» из шапки. Дата выбирается вручную,
 * после сохранения запись автоматически попадает в нужный месяц (ДДС
 * группирует по received_at/date).
 */
void TopCreateModal;
function TopCreateModal({ kind, allObjects, allCategories, ppIdByObjectId, onClose }: {
  kind: "income" | "expense";
  allObjects: ObjectRow[];
  allCategories: ExpenseCategoryRow[];
  ppIdByObjectId: Map<number, number>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const accent = kind === "income" ? "var(--green)" : "var(--brick)";
  const title = kind === "income" ? "Новый приход" : "Новый расход";

  const [draft, setDraft] = useState({
    date: today,
    object_id: null as number | null,
    text: "",  // counterparty (income) or description (expense)
    amount: 0,
    payment_method: "bank_transfer" as PaymentMethod,
    responsible_kind: null as ResponsibleKind | null,
    responsible_user_id: null as string | null,
    kind_cat: "other" as CategoryKind,  // для expense
  });

  const firstCatByKind: Partial<Record<CategoryKind, ExpenseCategoryRow>> = {};
  for (const c of allCategories) { if (!c.archived && !firstCatByKind[c.kind as CategoryKind]) firstCatByKind[c.kind as CategoryKind] = c; }

  const incomeMut = useMutation({
    mutationFn: (p: RevenuePayload) => createRevenue(p),
    onSuccess: () => { invalAllFinance(qc); onClose(); },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const expenseMut = useMutation({
    mutationFn: (p: ExpensePayload) => createExpense(p),
    onSuccess: () => { invalAllFinance(qc); onClose(); },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const pending = incomeMut.isPending || expenseMut.isPending;

  function save() {
    if (pending || !draft.amount || draft.amount <= 0) return;
    if (kind === "income") {
      incomeMut.mutate({
        received_at: draft.date,
        amount: draft.amount,
        type: "prepay",
        payment_method: draft.payment_method,
        object_id: draft.object_id,
        counterparty: draft.text || null,
        responsible_kind: draft.responsible_kind,
        responsible_user_id: draft.responsible_user_id,
      });
    } else {
      const cat = firstCatByKind[draft.kind_cat];
      if (!cat) { alert(`Не найдено категории «${KIND_LABELS[draft.kind_cat]}». Создайте через настройки.`); return; }
      expenseMut.mutate({
        date: draft.date,
        category_id: cat.id,
        amount: draft.amount,
        payment_method: draft.payment_method,
        object_id: draft.object_id,
        description: draft.text || null,
        responsible_kind: draft.responsible_kind,
        responsible_user_id: draft.responsible_user_id,
      });
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window"
           onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: 560, width: "92vw", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: accent }}>{title}</h2>
          <button className="btn btn-sm btn-ghost" onClick={onClose}><Icon name="x" size={12}/></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 12, color: "var(--mute)" }}>Дата *</label>
          <input type="date" className="input input-sm" value={draft.date}
                 onChange={(e) => setDraft(d => ({ ...d, date: e.target.value }))}
                 disabled={pending}/>

          <label style={{ fontSize: 12, color: "var(--mute)" }}>Объект</label>
          <ObjectCell value={draft.object_id} name={
            draft.object_id ? (allObjects.find(o => o.id === draft.object_id)?.client_name ?? null) : null
          } options={allObjects} ppIdByObjectId={ppIdByObjectId}
                      onChange={(oid) => setDraft(d => ({ ...d, object_id: oid }))}
                      disabled={pending}/>

          <label style={{ fontSize: 12, color: "var(--mute)" }}>{kind === "income" ? "За что" : "Статья / описание"}</label>
          <AutoGrowTextarea autoFocus className="input input-sm"
                 placeholder={kind === "income" ? "Контрагент / за что" : "Описание расхода"}
                 value={draft.text}
                 onChange={(e) => setDraft(d => ({ ...d, text: e.target.value }))}
                 onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); if (e.key === "Escape") onClose(); }}
                 style={{ fontSize: 12, fontWeight: 600 }}
                 disabled={pending}/>

          <label style={{ fontSize: 12, color: "var(--mute)" }}>Оплата</label>
          <PaymentMethodPicker value={draft.payment_method}
                               onChange={(pm) => setDraft(d => ({ ...d, payment_method: pm }))}
                               disabled={pending}/>

          {kind === "expense" && (
            <>
              <label style={{ fontSize: 12, color: "var(--mute)" }}>Категория</label>
              <CategoryPicker value={draft.kind_cat}
                              options={["fot","materials","outsource","taxes","marketing","sales","other"]}
                              onChange={(k) => setDraft(d => ({ ...d, kind_cat: k }))}
                              disabled={pending}/>
            </>
          )}

          <label style={{ fontSize: 12, color: "var(--mute)" }}>Ответственный</label>
          <ResponsiblePersonPicker valueUserId={draft.responsible_user_id}
                                   valueName={null}
                                   onChange={(uid, k) => setDraft(d => ({ ...d, responsible_user_id: uid, responsible_kind: k }))}
                                   disabled={pending}/>

          <label style={{ fontSize: 12, color: "var(--mute)" }}>Сумма ₽ *</label>
          <input className="input input-sm" type="number"
                 value={draft.amount || ""}
                 onChange={(e) => setDraft(d => ({ ...d, amount: parseFloat(e.target.value) || 0 }))}
                 onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }}
                 style={{ fontSize: 14, fontWeight: 700, color: accent, textAlign: "right" }}
                 disabled={pending}/>
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-sm btn-ghost" onClick={onClose} disabled={pending}>Отмена</button>
          <button className="btn btn-sm btn-primary" onClick={save}
                  disabled={!draft.amount || pending}>
            {pending ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}


void CreateIncomeCells;
function CreateIncomeCells({ ym, allObjects, ppIdByObjectId, onCreated, isVisible, incColCount }: {
  ym: string;  // "YYYY-MM"
  allObjects: ObjectRow[];
  ppIdByObjectId: Map<number, number>;
  onCreated: () => void;
  isVisible: (k: DdsColKey) => boolean;
  incColCount: number;
}) {
  const defaultDate = `${ym}-01`;
  const [creating, setCreating] = useState(false);
  const emptyDraft = {
    received_at: defaultDate,
    object_id: null as number | null,
    counterparty: "",
    amount: 0,
    payment_method: "bank_transfer" as PaymentMethod,
    responsible_kind: null as ResponsibleKind | null,
    responsible_user_id: null as string | null,
  };
  const [draft, setDraft] = useState(emptyDraft);

  const createMut = useMutation({
    mutationFn: (payload: RevenuePayload) => createRevenue(payload),
    onSuccess: () => {
      setCreating(false);
      setDraft(emptyDraft);
      onCreated();
    },
    onError: (e: Error) => alert("Ошибка создания: " + e.message),
  });

  function trySave() {
    if (createMut.isPending) return;
    if (!draft.amount || draft.amount <= 0) return;  // требуем ненулевую сумму
    createMut.mutate({
      received_at: draft.received_at,
      amount: draft.amount,
      type: "prepay",
      payment_method: draft.payment_method,
      object_id: draft.object_id,
      counterparty: draft.counterparty || null,
      responsible_kind: draft.responsible_kind,
      responsible_user_id: draft.responsible_user_id,
    });
  }

  function cancel() {
    setCreating(false);
    setDraft(emptyDraft);
  }

  if (!creating) {
    // Заглушка — клик в любую из видимых income-ячеек активирует режим создания
    const monthBg = MONTH_COLORS[ym.split("-")[1]];
    const cellStyle: React.CSSProperties = {
      cursor: "pointer", color: "var(--mute)",
      transition: "background 0.1s",
    };
    const incOrder: DdsColKey[] = ["inc_date","inc_object","inc_manager","inc_what","inc_pay","inc_resp","inc_amount"];
    const visIncs = incOrder.filter(isVisible);
    const lastK = visIncs[visIncs.length - 1];
    return (
      <>
        {visIncs.map(k => {
          const sep = k === lastK ? { borderRight: "2px solid var(--line)" } : {};
          const bg = k === "inc_date" ? { background: monthBg } : {};
          return (
            <td key={k} style={{ ...cellStyle, ...bg, ...sep }} onClick={() => setCreating(true)}
                title={k === "inc_object" ? "Кликните чтобы добавить приход" : undefined}>
              {k === "inc_object" && <span style={{ fontSize: 11, opacity: 0.4 }}>+ добавить</span>}
            </td>
          );
        })}
      </>
    );
  }

  return (
    <td colSpan={incColCount || 1} style={{
      padding: 0,
      borderRight: "2px solid var(--line)",
      position: "relative",
      overflow: "visible",
      height: 52,
    }}>
      {/* Плашка поверх — extends right beyond income half, чтобы влезло в одну строку */}
      <div style={{
        position: "absolute",
        top: 4, left: 6,
        width: "max-content", maxWidth: "calc(100vw - 40px)", minHeight: 44,
        background: "var(--card)",
        border: "1.5px solid var(--green)",
        borderRadius: 6,
        padding: "8px 12px",
        display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap",
        zIndex: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--green)", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
          Новый приход
        </span>
        <input
          type="date"
          className="input input-sm"
          value={draft.received_at}
          onChange={(e) => setDraft(d => ({ ...d, received_at: e.target.value }))}
          disabled={createMut.isPending}
          style={{ width: 130, fontSize: 12, flexShrink: 0 }}
          title="Дата"
        />
        <div style={{ width: 180, flexShrink: 0 }}>
          <ObjectCell value={draft.object_id} name={
            draft.object_id ? (allObjects.find(o => o.id === draft.object_id)?.client_name ?? null) : null
          } options={allObjects} ppIdByObjectId={ppIdByObjectId}
                      onChange={(oid) => setDraft(d => ({ ...d, object_id: oid }))}
                      disabled={createMut.isPending}/>
        </div>
        <AutoGrowTextarea
          autoFocus
          className="input input-sm"
          placeholder="За что (контрагент)"
          value={draft.counterparty}
          onChange={(e) => setDraft(d => ({ ...d, counterparty: e.target.value }))}
          disabled={createMut.isPending}
          style={{ flex: "1 1 160px", minWidth: 130, fontSize: 12, fontWeight: 600 }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); trySave(); } if (e.key === "Escape") cancel(); }}
          title="Контрагент / за что"
        />
        <div style={{ width: 100, flexShrink: 0 }}>
          <PaymentMethodPicker value={draft.payment_method}
                               onChange={(pm) => setDraft(d => ({ ...d, payment_method: pm }))}
                               disabled={createMut.isPending}/>
        </div>
        <div style={{ width: 130, flexShrink: 0 }}>
          <ResponsiblePersonPicker valueUserId={draft.responsible_user_id}
                                   valueName={null}
                                   onChange={(uid, kind) => setDraft(d => ({ ...d, responsible_user_id: uid, responsible_kind: kind }))}
                                   disabled={createMut.isPending}/>
        </div>
        <input
          className="input input-sm"
          type="number"
          placeholder="0"
          value={draft.amount || ""}
          onChange={(e) => setDraft(d => ({ ...d, amount: parseFloat(e.target.value) || 0 }))}
          disabled={createMut.isPending}
          style={{ width: 110, fontSize: 13, textAlign: "right", color: "var(--green)", fontWeight: 700, flexShrink: 0 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") trySave();
            if (e.key === "Escape") cancel();
          }}
          title="Сумма ₽"
        />
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={trySave}
          disabled={!draft.amount || createMut.isPending}
          style={{ flexShrink: 0, opacity: draft.amount ? 1 : 0.5 }}
        >
          {createMut.isPending ? "…" : "Сохранить"}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={cancel}
          disabled={createMut.isPending}
          style={{ flexShrink: 0 }}
          title="Отмена (Esc)"
        >
          <Icon name="x" size={12}/>
        </button>
      </div>
    </td>
  );
}


/**
 * Пустые expense-ячейки → клик → черновой расход через тот же overlay-стиль.
 * Required: amount > 0 + category_id (берём первую категорию выбранного kind).
 */
void CreateExpenseCells;
function CreateExpenseCells({ ym, allObjects, allCategories, ppIdByObjectId, onCreated, isVisible, expColCount }: {
  ym: string;
  allObjects: ObjectRow[];
  allCategories: ExpenseCategoryRow[];
  ppIdByObjectId: Map<number, number>;
  onCreated: () => void;
  isVisible: (k: DdsColKey) => boolean;
  expColCount: number;
}) {
  const defaultDate = `${ym}-01`;
  const [creating, setCreating] = useState(false);
  const emptyDraft = {
    date: defaultDate,
    object_id: null as number | null,
    description: "",
    amount: 0,
    payment_method: "bank_transfer" as PaymentMethod,
    kind: "other" as CategoryKind,
    responsible_kind: null as ResponsibleKind | null,
    responsible_user_id: null as string | null,
  };
  const [draft, setDraft] = useState(emptyDraft);

  // Первая категория каждого kind — нужно для сохранения (FK на категорию)
  const firstCatByKind: Partial<Record<CategoryKind, ExpenseCategoryRow>> = {};
  for (const c of allCategories) {
    if (c.archived) continue;
    const k = c.kind as CategoryKind;
    if (!firstCatByKind[k]) firstCatByKind[k] = c;
  }
  const KIND_OPTIONS: CategoryKind[] = ["fot", "materials", "outsource", "taxes", "marketing", "sales", "other"];

  const createMut = useMutation({
    mutationFn: (payload: ExpensePayload) => createExpense(payload),
    onSuccess: () => {
      setCreating(false);
      setDraft(emptyDraft);
      onCreated();
    },
    onError: (e: Error) => alert("Ошибка создания: " + e.message),
  });

  function trySave() {
    if (createMut.isPending) return;
    if (!draft.amount || draft.amount <= 0) return;
    const cat = firstCatByKind[draft.kind];
    if (!cat) { alert(`Не найдено категории типа «${KIND_LABELS[draft.kind]}». Создайте через настройки финансов.`); return; }
    createMut.mutate({
      date: draft.date,
      category_id: cat.id,
      amount: draft.amount,
      payment_method: draft.payment_method,
      object_id: draft.object_id,
      description: draft.description || null,
      responsible_kind: draft.responsible_kind,
      responsible_user_id: draft.responsible_user_id,
    });
  }

  function cancel() {
    setCreating(false);
    setDraft(emptyDraft);
  }

  if (!creating) {
    const monthBg = MONTH_COLORS[ym.split("-")[1]];
    const cellStyle: React.CSSProperties = {
      cursor: "pointer", color: "var(--mute)",
      transition: "background 0.1s",
    };
    const expOrder: DdsColKey[] = ["exp_date","exp_object","exp_what","exp_pay","exp_kind","exp_resp","exp_amount"];
    const visExps = expOrder.filter(isVisible);
    return (
      <>
        {visExps.map(k => {
          const bg = k === "exp_date" ? { background: monthBg } : {};
          return (
            <td key={k} style={{ ...cellStyle, ...bg }} onClick={() => setCreating(true)}
                title={k === "exp_what" ? "Кликните чтобы добавить расход" : undefined}>
              {k === "exp_what" && <span style={{ fontSize: 11, opacity: 0.4 }}>+ добавить</span>}
            </td>
          );
        })}
      </>
    );
  }

  return (
    <td colSpan={expColCount || 1} style={{
      padding: 0, position: "relative", overflow: "visible", height: 52,
    }}>
      <div style={{
        position: "absolute",
        top: 4, right: 6,
        width: "max-content", maxWidth: "calc(100vw - 40px)", minHeight: 44,
        background: "var(--card)",
        border: "1.5px solid var(--brick)",
        borderRadius: 6,
        padding: "8px 12px",
        display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap",
        zIndex: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--brick)", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
          Новый расход
        </span>
        <input
          type="date"
          className="input input-sm"
          value={draft.date}
          onChange={(e) => setDraft(d => ({ ...d, date: e.target.value }))}
          disabled={createMut.isPending}
          style={{ width: 130, fontSize: 12, flexShrink: 0 }}
          title="Дата"
        />
        <div style={{ width: 180, flexShrink: 0 }}>
          <ObjectCell value={draft.object_id} name={
            draft.object_id ? (allObjects.find(o => o.id === draft.object_id)?.client_name ?? null) : null
          } options={allObjects} ppIdByObjectId={ppIdByObjectId}
                      onChange={(oid) => setDraft(d => ({ ...d, object_id: oid }))}
                      disabled={createMut.isPending}/>
        </div>
        <AutoGrowTextarea
          autoFocus
          className="input input-sm"
          placeholder="Статья / описание"
          value={draft.description}
          onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
          disabled={createMut.isPending}
          style={{ flex: "1 1 160px", minWidth: 130, fontSize: 12, fontWeight: 600 }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); trySave(); } if (e.key === "Escape") cancel(); }}
          title="Статья / описание"
        />
        <div style={{ width: 100, flexShrink: 0 }}>
          <PaymentMethodPicker value={draft.payment_method}
                               onChange={(pm) => setDraft(d => ({ ...d, payment_method: pm }))}
                               disabled={createMut.isPending}/>
        </div>
        <div style={{ width: 130, flexShrink: 0 }}>
          <CategoryPicker value={draft.kind}
                          options={KIND_OPTIONS}
                          onChange={(k) => setDraft(d => ({ ...d, kind: k }))}
                          disabled={createMut.isPending}/>
        </div>
        <div style={{ width: 160, flexShrink: 0 }}>
          <ResponsiblePersonPicker valueUserId={draft.responsible_user_id}
                                   valueName={null}
                                   onChange={(uid, kind) => setDraft(d => ({ ...d, responsible_user_id: uid, responsible_kind: kind }))}
                                   disabled={createMut.isPending}/>
        </div>
        <input
          className="input input-sm"
          type="number"
          placeholder="0"
          value={draft.amount || ""}
          onChange={(e) => setDraft(d => ({ ...d, amount: parseFloat(e.target.value) || 0 }))}
          disabled={createMut.isPending}
          style={{ width: 110, fontSize: 13, textAlign: "right", color: "var(--brick)", fontWeight: 700, flexShrink: 0 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") trySave();
            if (e.key === "Escape") cancel();
          }}
          title="Сумма ₽"
        />
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={trySave}
          disabled={!draft.amount || createMut.isPending}
          style={{ flexShrink: 0, opacity: draft.amount ? 1 : 0.5 }}
        >
          {createMut.isPending ? "…" : "Сохранить"}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={cancel}
          disabled={createMut.isPending}
          style={{ flexShrink: 0 }}
          title="Отмена (Esc)"
        >
          <Icon name="x" size={12}/>
        </button>
      </div>
    </td>
  );
}


/** Текстовая область с авто-ростом по высоте при вводе (как поле ввода в чате). */
function AutoGrowTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  function autoSize() {
    const el = ref.current; if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }
  useEffect(() => { autoSize(); }, [props.value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      onInput={autoSize}
      {...props}
      style={{
        resize: "none", overflow: "hidden",
        lineHeight: 1.35,
        ...props.style,
      }}
    />
  );
}


function PaymentMethodPicker({ value, onChange, disabled }: {
  value: PaymentMethod;
  onChange: (pm: PaymentMethod) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const PM_COLORS: Record<PaymentMethod, string> = {
    cash:           "#3D7A3D",  // зелёный — наличка
    bank_transfer:  "#5d4ec1",  // фиолетовый — безнал
    card_transfer:  "#2E5BA0",  // синий — карта
  };
  const PM_SHORT: Record<PaymentMethod, string> = {
    cash:           "Нал",
    bank_transfer:  "Безнал",
    card_transfer:  "Карта",
  };
  const opts: PaymentMethod[] = ["cash", "bank_transfer", "card_transfer"];
  const color = PM_COLORS[value];
  return (
    <div style={{ position: "relative", display: "block", width: "100%", minWidth: 0 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(v => !v); }}
        disabled={disabled}
        style={{
          background: color, color: "#fff", border: "none", borderRadius: 14,
          padding: "4px 10px", fontSize: 11, fontWeight: 600,
          cursor: disabled ? "wait" : "pointer",
          display: "flex", alignItems: "center", gap: 4,
          opacity: disabled ? 0.6 : 1, width: "100%", overflow: "hidden",
          textAlign: "left", justifyContent: "space-between",
        }}
        title="Изменить способ оплаты"
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {PM_SHORT[value]}
        </span>
        <span style={{ fontSize: 9, opacity: 0.85, flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 100 }}/>
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 6, padding: 4, zIndex: 101,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            display: "flex", flexDirection: "column", gap: 2, minWidth: 130,
          }}>
            {opts.map(pm => (
              <button
                key={pm}
                type="button"
                onClick={() => { onChange(pm); setOpen(false); }}
                style={{
                  background: pm === value ? PM_COLORS[pm] : "transparent",
                  color: pm === value ? "#fff" : "var(--ink-2)",
                  border: "none", padding: "5px 10px",
                  fontSize: 12, fontWeight: 500,
                  cursor: "pointer", textAlign: "left",
                  borderRadius: 4,
                  display: "flex", alignItems: "center", gap: 8,
                }}
                onMouseEnter={(e) => { if (pm !== value) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
                onMouseLeave={(e) => { if (pm !== value) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: PM_COLORS[pm] }}/>
                {PAYMENT_LABELS[pm]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


/**
 * Picker конкретного человека (выездной инженер / собственник). Подгружает
 * группированный список из бэка. На выбор сохраняет user_id + kind.
 */
function ResponsiblePersonPicker({ valueUserId, valueName, onChange, disabled }: {
  valueUserId: string | null;
  valueName: string | null;  // отображаемое имя (если уже сохранено)
  onChange: (user_id: string | null, kind: ResponsibleKind | null) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const q = useQuery<ResponsibleGrouped>({
    queryKey: ["finance-responsibles-grouped"],
    queryFn: fetchResponsiblesGrouped,
    staleTime: 5 * 60 * 1000,
  });
  const data = q.data || { engineers: [], owners: [] };
  const allPeople = [...data.engineers.map(p => ({ ...p, kind: "engineer" as const })),
                     ...data.owners.map(p => ({ ...p, kind: "owner" as const }))];
  const current = allPeople.find(p => p.id === valueUserId);
  const currentKind: ResponsibleKind | null = current?.kind ?? null;
  const display = current?.name ?? valueName ?? "—";
  const color = currentKind ? RESPONSIBLE_KIND_COLORS[currentKind] : "var(--mute)";
  return (
    <div style={{ position: "relative", display: "block", width: "100%", minWidth: 0 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(v => !v); }}
        disabled={disabled}
        style={{
          background: currentKind ? color : "var(--paper-2)",
          color: currentKind ? "#fff" : "var(--mute)",
          border: currentKind ? "none" : "1px dashed var(--line)",
          borderRadius: 14, padding: "4px 10px", fontSize: 11, fontWeight: 600,
          cursor: disabled ? "wait" : "pointer",
          display: "flex", alignItems: "center", gap: 4,
          opacity: disabled ? 0.6 : 1, width: "100%", overflow: "hidden",
          textAlign: "left", justifyContent: "space-between",
        }}
        title="Выбрать ответственного"
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {display}
        </span>
        <span style={{ fontSize: 9, opacity: 0.85, flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 100 }}/>
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 6, padding: 6, zIndex: 101,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            display: "flex", flexDirection: "column", gap: 2, minWidth: 200, maxHeight: 320, overflowY: "auto",
          }}>
            {/* Группа: Выездные инженеры */}
            {data.engineers.length > 0 && (
              <>
                <div style={{ padding: "4px 8px", fontSize: 10, fontWeight: 700, color: RESPONSIBLE_KIND_COLORS.engineer, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Выездные инженеры
                </div>
                {data.engineers.map(p => (
                  <button key={p.id} type="button"
                          onClick={() => { onChange(p.id, "engineer"); setOpen(false); }}
                          style={{
                            background: p.id === valueUserId ? RESPONSIBLE_KIND_COLORS.engineer : "transparent",
                            color: p.id === valueUserId ? "#fff" : "var(--ink-2)",
                            border: "none", padding: "5px 10px", fontSize: 12, fontWeight: 500,
                            cursor: "pointer", textAlign: "left", borderRadius: 4,
                            display: "flex", alignItems: "center", gap: 8,
                          }}
                          onMouseEnter={(e) => { if (p.id !== valueUserId) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
                          onMouseLeave={(e) => { if (p.id !== valueUserId) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: RESPONSIBLE_KIND_COLORS.engineer }}/>
                    {p.name}
                  </button>
                ))}
              </>
            )}
            {/* Группа: Собственники */}
            {data.owners.length > 0 && (
              <>
                <div style={{ padding: "4px 8px", fontSize: 10, fontWeight: 700, color: RESPONSIBLE_KIND_COLORS.owner, textTransform: "uppercase", letterSpacing: 0.4, marginTop: data.engineers.length > 0 ? 4 : 0 }}>
                  Собственники
                </div>
                {data.owners.map(p => (
                  <button key={p.id} type="button"
                          onClick={() => { onChange(p.id, "owner"); setOpen(false); }}
                          style={{
                            background: p.id === valueUserId ? RESPONSIBLE_KIND_COLORS.owner : "transparent",
                            color: p.id === valueUserId ? "#fff" : "var(--ink-2)",
                            border: "none", padding: "5px 10px", fontSize: 12, fontWeight: 500,
                            cursor: "pointer", textAlign: "left", borderRadius: 4,
                            display: "flex", alignItems: "center", gap: 8,
                          }}
                          onMouseEnter={(e) => { if (p.id !== valueUserId) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
                          onMouseLeave={(e) => { if (p.id !== valueUserId) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: RESPONSIBLE_KIND_COLORS.owner }}/>
                    {p.name}
                  </button>
                ))}
              </>
            )}
            {data.engineers.length === 0 && data.owners.length === 0 && (
              <div style={{ padding: 12, fontSize: 11, color: "var(--mute)", textAlign: "center" }}>
                Нет пользователей с ролями «Выездной инженер» или «Собственник».<br/>
                Назначьте роли в настройках.
              </div>
            )}
            {/* Сброс */}
            <div style={{ borderTop: "1px solid var(--line)", marginTop: 4, paddingTop: 4 }}>
              <button type="button"
                      onClick={() => { onChange(null, null); setOpen(false); }}
                      style={{
                        width: "100%", background: "transparent", border: "none",
                        padding: "5px 10px", fontSize: 11, color: "var(--mute)",
                        cursor: "pointer", textAlign: "left", borderRadius: 4,
                      }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = "transparent"}>
                — без ответственного —
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


void ResponsibleKindPicker;
function ResponsibleKindPicker({ value, onChange, disabled }: {
  value: ResponsibleKind | null;
  onChange: (k: ResponsibleKind | null) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const color = value ? RESPONSIBLE_KIND_COLORS[value] : "var(--mute)";
  const label = value ? RESPONSIBLE_KIND_LABELS[value] : "—";
  const options: (ResponsibleKind | null)[] = ["engineer", "owner", null];
  return (
    <div style={{ position: "relative", display: "block", width: "100%" }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(v => !v); }}
        disabled={disabled}
        style={{
          background: value ? color : "var(--paper-2)",
          color: value ? "#fff" : "var(--mute)",
          border: value ? "none" : "1px dashed var(--line)",
          borderRadius: 14, padding: "4px 10px", fontSize: 11, fontWeight: 600,
          cursor: disabled ? "wait" : "pointer",
          display: "flex", alignItems: "center", gap: 4,
          opacity: disabled ? 0.6 : 1, width: "100%",
          textAlign: "left", justifyContent: "space-between",
        }}
        title="Изменить ответственного"
      >
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{ fontSize: 9, opacity: 0.85 }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 100 }}/>
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 6, padding: 4, zIndex: 101,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            display: "flex", flexDirection: "column", gap: 2, minWidth: 140,
          }}>
            {options.map((k, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { onChange(k); setOpen(false); }}
                style={{
                  background: k === value ? (k ? RESPONSIBLE_KIND_COLORS[k] : "var(--paper-2)") : "transparent",
                  color: k === value && k ? "#fff" : "var(--ink-2)",
                  border: "none", padding: "5px 10px",
                  fontSize: 12, fontWeight: 500,
                  cursor: "pointer", textAlign: "left",
                  borderRadius: 4,
                  display: "flex", alignItems: "center", gap: 8,
                }}
                onMouseEnter={(e) => { if (k !== value) (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
                onMouseLeave={(e) => { if (k !== value) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                {k && <span style={{ width: 10, height: 10, borderRadius: "50%", background: RESPONSIBLE_KIND_COLORS[k] }}/>}
                {!k && <span style={{ width: 10, height: 10, borderRadius: "50%", border: "1px dashed var(--mute)" }}/>}
                {k ? RESPONSIBLE_KIND_LABELS[k] : "Не указан"}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


/** Палитра по месяцам — каждый месяц со своим мягким оттенком (для ячейки даты в ДДС). */
const MONTH_COLORS: Record<string, string> = {
  "01": "rgba(174,201,232,0.30)",  // янв — голубой
  "02": "rgba(196,176,232,0.30)",  // фев — лиловый
  "03": "rgba(178,223,178,0.30)",  // мар — салатовый
  "04": "rgba(255,229,180,0.40)",  // апр — амбер
  "05": "rgba(255,193,210,0.35)",  // май — розовый
  "06": "rgba(178,232,229,0.35)",  // июн — бирюза
  "07": "rgba(247,224,138,0.40)",  // июл — жёлтый
  "08": "rgba(255,200,150,0.40)",  // авг — оранжевый
  "09": "rgba(220,196,232,0.35)",  // сен — фиолетовый
  "10": "rgba(255,170,150,0.35)",  // окт — терракотовый
  "11": "rgba(180,196,232,0.35)",  // ноя — индиго
  "12": "rgba(170,224,247,0.35)",  // дек — небесный
};
function monthBgColor(r: TransactionRow): string | undefined {
  const raw = r.kind === "income" ? r.received_at : r.date;
  if (!raw || raw.length < 7) return undefined;
  const m = raw.slice(5, 7);
  return MONTH_COLORS[m];
}

/** Таблица транзакций — компактная: дата, объект, за что/статья, ответственный, сумма.
 *  Внутри таблицы вставляются строки-разделители «Месяц + итог» при смене месяца. */
void TxTable;
function TxTable({ rows, kind }: { rows: TransactionRow[]; kind: "income" | "expense" }) {
  if (rows.length === 0) {
    return <div className="muted" style={{ padding: 14, fontSize: 12 }}>Операций нет</div>;
  }
  const total = rows.reduce((s, r) => s + r.amount, 0);

  // Группируем строки по YYYY-MM, в порядке (новые сверху)
  const byMonth = new Map<string, TransactionRow[]>();
  for (const r of rows) {
    const raw = kind === "income" ? r.received_at : r.date;
    if (!raw || raw.length < 7) continue;
    const ym = raw.slice(0, 7);
    if (!byMonth.has(ym)) byMonth.set(ym, []);
    byMonth.get(ym)!.push(r);
  }
  const monthsOrdered = Array.from(byMonth.keys()).sort().reverse();

  return (
    <div className="tbl-scroll" style={{ overflowX: "auto" }}>
      <table className="tbl" style={{ fontSize: 12, minWidth: 600 }}>
        <thead>
          <tr>
            <th style={{ width: 80 }}>Дата</th>
            <th>Объект</th>
            <th>{kind === "income" ? "За что" : "Статья"}</th>
            <th style={{ width: 90 }}>Ответств.</th>
            <th className="num" style={{ width: 100 }}>Сумма</th>
          </tr>
        </thead>
        <tbody>
          {monthsOrdered.map(ym => {
            const monthRows = byMonth.get(ym)!;
            const monthTotal = monthRows.reduce((s, r) => s + r.amount, 0);
            const [y, m] = ym.split("-");
            const monthLabel = `${RU_MONTH_TX[Number(m) - 1]} ${y}`;
            const bg = monthBgColor(monthRows[0]);
            return (
              <Fragment key={ym}>
                <tr style={{ background: bg }}>
                  <td colSpan={4} style={{ fontWeight: 700, fontSize: 12.5, padding: "8px 14px", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {monthLabel}
                  </td>
                  <td className="num tabular" style={{ fontWeight: 700, color: kind === "income" ? "var(--green)" : "var(--brick)", padding: "8px 14px" }}>
                    {formatRub(monthTotal)}
                  </td>
                </tr>
                {monthRows.map(r => (
                  <tr key={`${r.kind}-${r.id}`}>
                    <td className="tabular" style={{ background: monthBgColor(r), fontWeight: 500 }}>{fmtTxDate(r)}</td>
              <td>
                {r.object_name ? <span>{r.object_name}</span> : <span className="muted">—</span>}
                {r.region && <div style={{ fontSize: 10, color: "var(--mute)" }}>{r.region}</div>}
              </td>
              <td>
                {kind === "income" ? (
                  <>
                    <strong>{r.counterparty || "—"}</strong>
                    <div style={{ fontSize: 11, color: "var(--mute)" }}>
                      {r.type ? REVENUE_TYPE_LABELS[r.type] : ""}
                      {" · "}{PAYMENT_LABELS[r.payment_method]}
                      {r.account_name && <span> · {r.account_name}</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <strong>{r.category_name || "—"}</strong>
                    {r.category_kind && <span className="badge" style={{ marginLeft: 6, fontSize: 10 }}>{KIND_LABELS[r.category_kind]}</span>}
                    <div style={{ fontSize: 11, color: "var(--mute)" }}>
                      {r.description}
                      {r.supplier && <span> · {r.supplier}</span>}
                      {" · "}{PAYMENT_LABELS[r.payment_method]}
                    </div>
                  </>
                )}
              </td>
              <td style={{ fontSize: 11 }}>
                {r.created_by_name || <span className="muted">—</span>}
              </td>
                    <td className="num strong tabular" style={{ color: kind === "income" ? "var(--green)" : "var(--brick)" }}>
                      {formatRub(r.amount)}
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
          <tr style={{ borderTop: "2px solid var(--line)", fontWeight: 700, background: "var(--paper-2)" }}>
            <td colSpan={4}>Итого</td>
            <td className="num tabular" style={{ color: kind === "income" ? "var(--green)" : "var(--brick)" }}>{formatRub(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
